import { api } from './lib/api.js';
import {
  listDevices,
  populateDeviceSelect,
  requestPreflightStream,
  stopStream,
  createMicActivityIndicator,
} from './lib/media.js';
import { SessionView } from './lib/session.js';
import { loadDevicePreference, savePreferredDevice } from './lib/device-prefs.js';

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => document.querySelectorAll(sel);

function tenantPrefix() {
  const slug = document.documentElement.dataset.tenantSlug || '';
  return slug ? `/${slug}` : '';
}

let devicePreviewCleanup = null;
let micActivityCleanup = null;

let state = {
  user: null,
  permissions: [],
  roles: [],
  memberships: [],
  orgId: null,
  orgName: '',
  providerId: null,
  currentPage: 'dashboard',
  sessionPoll: null,
  notifications: {
    unread: 0,
    items: [],
    drawerOpen: false,
    filterStatus: 'all',
    filterType: 'all',
    page: 0,
    limit: 20,
    total: 0,
    poll: null,
  },
  schedule: {
    view: 'week',
    date: todayStr(),
    sessions: [],
    start: null,
    end: null,
    loading: false,
  },
};

function clearError() {
  const el = $('#staff-error');
  if (el) el.textContent = '';
}

function setError(msg) {
  const el = $('#staff-error');
  if (el) el.textContent = msg || '';
}

function showLogin() {
  $('#login-section').hidden = false;
  $('#app-shell').hidden = true;
  stopNotificationPoll();
}

function showApp() {
  $('#login-section').hidden = true;
  $('#app-shell').hidden = false;
  startNotificationPoll();
  renderNotificationBell();
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatAssignmentDate(iso) {
  if (!iso) return '';
  const datePart = String(iso).split('T')[0];
  const d = new Date(`${datePart}T00:00:00`);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function hasPermission(name) {
  return state.permissions.includes(name) || state.permissions.includes('platform.manage');
}

function hasRole(name) {
  return state.roles.some((r) => r.name === name);
}

function isProvider() {
  return !!state.providerId;
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

function startNotificationPoll() {
  if (state.notifications.poll) return;
  loadUnreadCount();
  state.notifications.poll = setInterval(loadUnreadCount, 30000);
}

function stopNotificationPoll() {
  if (state.notifications.poll) {
    clearInterval(state.notifications.poll);
    state.notifications.poll = null;
  }
}

async function loadUnreadCount() {
  try {
    const data = await api('/notifications/unread-count');
    state.notifications.unread = data.count || 0;
    renderNotificationBell();
  } catch (_err) {
    // Non-fatal: do not disturb the user for a polling failure.
  }
}

function renderNotificationBell() {
  const btn = $('#notification-btn');
  const count = $('#notification-count');
  if (!btn || !count) return;
  if (!state.user) {
    btn.hidden = true;
    return;
  }
  btn.hidden = false;
  const n = state.notifications.unread || 0;
  count.textContent = n > 99 ? '99+' : String(n);
  count.hidden = n === 0;
}

function openDrawer() {
  const drawer = $('#notification-drawer');
  const backdrop = $('#notification-drawer-backdrop');
  if (!drawer || !backdrop) return;
  drawer.classList.add('open');
  backdrop.classList.add('open');
  drawer.setAttribute('aria-hidden', 'false');
  state.notifications.drawerOpen = true;
  loadDrawerNotifications();
}

function closeDrawer() {
  const drawer = $('#notification-drawer');
  const backdrop = $('#notification-drawer-backdrop');
  if (!drawer || !backdrop) return;
  drawer.classList.remove('open');
  backdrop.classList.remove('open');
  drawer.setAttribute('aria-hidden', 'true');
  state.notifications.drawerOpen = false;
}

function relativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function typeLabel(type) {
  const map = {
    participant_reassigned: 'Participant changes',
    session_coverage: 'Session coverage',
    supervisor_requested: 'Clinical supervision',
    supervisor_authorized: 'Clinical supervision',
    supervisor_denied: 'Clinical supervision',
    supervisor_must_leave: 'Clinical supervision',
    provider_status_changed: 'Administrative',
    user_membership_status_changed: 'Administrative',
    supervisor_assignment_changed: 'Clinical supervision',
    document_distributed: 'Documents',
  };
  return map[type] || 'Operational';
}

function typeIconClass(type) {
  if (type === 'session_coverage') return 'notification-icon-green';
  if (type === 'supervisor_authorized') return 'notification-icon-green';
  if (type === 'supervisor_must_leave' || type === 'supervisor_denied') return 'notification-icon-red';
  if (type.startsWith('supervisor_')) return 'notification-icon-amber';
  if (type === 'participant_reassigned') return 'notification-icon-blue';
  if (type === 'document_distributed') return 'notification-icon-gray';
  return 'notification-icon-gray';
}

function typeInitial(type) {
  if (type === 'participant_reassigned') return 'P';
  if (type === 'session_coverage') return 'S';
  if (type.startsWith('supervisor_')) return 'C';
  if (type === 'document_distributed') return 'D';
  return 'A';
}

async function markNotificationRead(id, status, event) {
  if (event) event.stopPropagation();
  try {
    await api(`/notifications/${id}/${status === 'unread' ? 'read' : 'unread'}`, { method: 'PATCH' });
    await loadUnreadCount();
    if (state.notifications.drawerOpen) loadDrawerNotifications();
    if (state.currentPage === 'notifications') loadNotificationPage();
  } catch (_err) {
    // Ignore; the UI will refresh on next poll.
  }
}

async function markAllRead(event) {
  if (event) event.stopPropagation();
  try {
    await api('/notifications/mark-all-read', { method: 'POST' });
    await loadUnreadCount();
    if (state.notifications.drawerOpen) loadDrawerNotifications();
    if (state.currentPage === 'notifications') loadNotificationPage();
  } catch (_err) {
    // Ignore.
  }
}

async function loadDrawerNotifications() {
  const body = $('#notification-drawer-body');
  if (!body) return;
  try {
    const data = await api('/notifications?limit=20');
    const items = data.notifications || [];
    if (!items.length) {
      body.innerHTML = '<div class="drawer-empty">No notifications.</div>';
      return;
    }
    body.innerHTML = '';
    items.forEach((n) => {
      const el = document.createElement('div');
      el.className = `drawer-item ${n.status === 'unread' ? 'unread' : ''}`;
      el.innerHTML = `
        <div class="drawer-item-title">
          <span>${escapeHtml(n.title)}</span>
          ${n.status === 'unread' ? `<button class="btn btn-sm mark-read" data-id="${escapeHtml(n.id)}">Mark read</button>` : ''}
        </div>
        <div class="drawer-item-text">${escapeHtml(n.message)}</div>
        <div class="drawer-item-time">${relativeTime(n.created_at)} · ${typeLabel(n.type)}</div>
      `;
      el.addEventListener('click', (e) => {
        if (e.target.classList.contains('mark-read')) {
          markNotificationRead(n.id, 'unread', e);
          return;
        }
        if (n.status === 'unread') markNotificationRead(n.id, 'unread', e);
      });
      body.appendChild(el);
    });
    body.querySelectorAll('.mark-read').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = btn.dataset.id;
        markNotificationRead(id, 'unread', e);
      });
    });
  } catch (_err) {
    body.innerHTML = '<div class="drawer-empty">Unable to load notifications.</div>';
  }
}

async function refreshAuth() {
  try {
    const me = await api('/auth/me');
    if (!me.authenticated) {
      showLogin();
      return false;
    }
    state.user = me;
    state.orgId = me.organizationId;
    state.providerId = me.providerId;
    state.roles = me.roles || [];
    state.memberships = me.memberships || [];
    const currentOrg = state.memberships.find((m) => m.organization_id === state.orgId);
    state.orgName = currentOrg ? currentOrg.organization_name : '';
    const perms = await api('/auth/permissions');
    state.permissions = perms.permissions || [];
    showApp();
    renderShell();
    return true;
  } catch (err) {
    showLogin();
    return false;
  }
}

function renderShell() {
  // Topbar
  const orgSelect = $('#org-select');
  if (orgSelect) {
    orgSelect.innerHTML = '';
    state.memberships.forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m.organization_id;
      opt.textContent = m.organization_name;
      if (m.organization_id === state.orgId) opt.selected = true;
      orgSelect.appendChild(opt);
    });
    orgSelect.hidden = state.memberships.length <= 1;
    orgSelect.style.display = state.memberships.length <= 1 ? 'none' : '';
  }
  $('#topbar-org-name').textContent = state.orgName || (hasPermission('platform.manage') ? 'Platform administration' : 'No organization');
  $('#topbar-org-name').hidden = state.memberships.length > 1;
  $('#topbar-user').textContent = state.user.displayName || state.user.username;
  renderNotificationBell();

  // Sidebar
  const nav = $('#staff-nav');
  if (!nav) return;
  nav.innerHTML = '';

  const addNav = (id, label, icon = '') => {
    const btn = document.createElement('button');
    btn.className = 'nav-button';
    btn.dataset.page = id;
    btn.textContent = `${icon} ${label}`.trim();
    if (id === state.currentPage) btn.classList.add('active');
    btn.addEventListener('click', () => navigateTo(id));
    nav.appendChild(btn);
  };

  // Provider-only compact nav
  if (isProvider() && !hasPermission('organization.manage')) {
    addNav('my-sessions', 'My sessions');
    addNav('my-groups', 'My groups');
    addNav('schedule', 'Schedule');
    addNav('documents', 'Documents');
    addNav('devices', 'Devices');
  }

  // Org admin / scheduler nav
  if (hasPermission('organization.manage') || hasPermission('schedule.view') || hasPermission('user.manage')) {
    addNav('dashboard', 'Dashboard');
    addNav('schedule', 'Schedule');
    addNav('clients', 'Clients');
    addNav('providers', 'Providers');
    addNav('documents', 'Documents');
    addNav('users', 'Users');
    if (hasPermission('supervisor.assignment.manage')) addNav('supervisors', 'Supervisors');
    if (hasPermission('role.manage')) addNav('roles', 'Roles & permissions');
    if (hasPermission('acknowledgment.manage')) addNav('acknowledgments', 'Acknowledgments');
    if (hasPermission('audit.view')) addNav('audit', 'Audit log');
    addNav('notifications', 'Notifications');
    if (hasPermission('organization.manage')) addNav('settings', 'Organization settings');
    addNav('devices', 'Devices');
  }

  // Supervisor nav
  if (hasPermission('supervisor.observe')) {
    addNav('supervision', 'Supervision');
  }

  // Platform admin
  if (hasPermission('platform.manage')) {
    addNav('platform', 'Platform administration');
  }
}

async function switchOrg(orgId) {
  try {
    await api('/auth/context', { method: 'POST', body: { organizationId: orgId } });
    await refreshAuth();
    navigateTo(state.currentPage);
  } catch (err) {
    setError('Unable to switch organization.');
  }
}

function navigateTo(page) {
  stopDevicePreview();
  state.currentPage = page;
  renderShell();
  const main = $('#staff-main');
  if (!main) return;
  main.innerHTML = '';
  clearError();
  stopSessionPoll();
  switch (page) {
    case 'dashboard': renderDashboard(main); break;
    case 'my-sessions': renderProviderConsole(main); break;
    case 'my-groups': renderMyGroups(main); break;
    case 'schedule': renderSchedule(main); break;
    case 'clients': renderClients(main); break;
    case 'providers': renderProviders(main); break;
    case 'documents': renderDocuments(main); break;
    case 'users': renderUsers(main); break;
    case 'supervisors': renderSupervisors(main); break;
    case 'roles': renderRoles(main); break;
    case 'acknowledgments': renderAcknowledgments(main); break;
    case 'audit': renderAudit(main); break;
    case 'notifications': renderNotifications(main); break;
    case 'settings': renderSettings(main); break;
    case 'devices': renderDevices(main); break;
    case 'supervision': renderSupervision(main); break;
    case 'platform': renderPlatformAdmin(main); break;
    default: renderDashboard(main);
  }
}

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                  */
/* -------------------------------------------------------------------------- */
async function renderDashboard(container) {
  container.innerHTML = `
    <h1 class="page-title">Dashboard</h1>
    <p class="page-description">Overview of today's Telehealth Platform operations.</p>
    <div class="summary-grid" id="dash-summary">
      <div class="summary-card"><div class="summary-label">Today's sessions</div><div class="summary-number" id="dash-sessions">-</div></div>
      <div class="summary-card"><div class="summary-label">Active now</div><div class="summary-number" id="dash-active">-</div></div>
      <div class="summary-card"><div class="summary-label">Clients scheduled</div><div class="summary-number" id="dash-clients">-</div></div>
      <div class="summary-card"><div class="summary-label">Needs attention</div><div class="summary-number" id="dash-attention">-</div></div>
    </div>
    <div class="panel">
      <button class="panel-header" id="dash-today-header"><span>Today's sessions</span><span id="dash-today-count">▾</span></button>
      <div class="panel-body" id="dash-today-body"></div>
    </div>
  `;
  $('#dash-today-header').addEventListener('click', () => togglePanel('dash-today-body'));
  try {
    const today = todayStr();
    const providerOnly = isProvider() && !hasPermission('organization.manage');
    const sessionsRes = providerOnly
      ? await api(`/sessions?date=${today}`)
      : await api(`/admin/organizations/${state.orgId}/sessions?date=${today}`);
    const sessions = sessionsRes.sessions || [];
    let clients = [];
    if (!providerOnly) {
      try {
        const clientsRes = await api(`/admin/organizations/${state.orgId}/clients`);
        clients = clientsRes.clients || [];
      } catch (_e) { /* non-fatal for dashboard */ }
    }
    const active = sessions.filter((s) => s.status === 'open' || s.status === 'in_progress').length;
    const attention = sessions.filter((s) => s.status === 'scheduled' && !s.actualProviderId).length;
    $('#dash-sessions').textContent = sessions.length;
    $('#dash-active').textContent = active;
    $('#dash-clients').textContent = clients.length;
    $('#dash-attention').textContent = attention;
    $('#dash-today-count').textContent = `(${sessions.length})`;
    renderSessionTable($('#dash-today-body'), sessions, { showActions: true });
  } catch (err) {
    setError('Unable to load dashboard data.');
  }
}

/* -------------------------------------------------------------------------- */
/* Provider console                                                           */
/* -------------------------------------------------------------------------- */
async function renderProviderConsole(container) {
  container.innerHTML = `
    <h1 class="page-title">My sessions</h1>
    <p class="page-description">Today's assigned Telehealth Platform sessions and coverage assignments.</p>
    <div class="summary-grid" id="provider-summary">
      <div class="summary-card"><div class="summary-label">Today's sessions</div><div class="summary-number" id="prov-sessions">-</div></div>
      <div class="summary-card"><div class="summary-label">Active now</div><div class="summary-number" id="prov-active">-</div></div>
      <div class="summary-card"><div class="summary-label">Waiting</div><div class="summary-number" id="prov-waiting">-</div></div>
      <div class="summary-card"><div class="summary-label">Coverage assignments</div><div class="summary-number" id="prov-coverage">-</div></div>
    </div>
    <div class="panel">
      <div class="panel-header">Today's sessions</div>
      <div class="panel-body" id="provider-sessions-body"></div>
    </div>
  `;
  await loadProviderSessions();
  state.sessionPoll = setInterval(loadProviderSessions, 5000);
}

async function loadProviderSessions() {
  try {
    const date = todayStr();
    const data = await api(`/sessions?date=${date}`);
    const sessions = data.sessions || [];
    const scheduled = data.scheduled || [];
    const coverage = data.coverage || [];
    const active = sessions.filter((s) => s.status === 'open' || s.status === 'in_progress').length;
    const waitingMap = await countWaitingByRoom(sessions.map((s) => s.roomCode));
    const waitingTotal = Object.values(waitingMap).reduce((a, b) => a + b, 0);
    $('#prov-sessions').textContent = sessions.length;
    $('#prov-active').textContent = active;
    $('#prov-waiting').textContent = waitingTotal;
    $('#prov-coverage').textContent = coverage.length;
    const body = $('#provider-sessions-body');
    if (!body) return;
    if (!sessions.length) {
      body.innerHTML = '<p class="empty-state">No sessions assigned for today.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead>
        <tr><th>Time</th><th>Session</th><th>Assignment</th><th>Participants</th><th>Status</th><th class="actions-cell"></th></tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    sessions.forEach((s) => {
      const isCoverage = s.isCoverage;
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${formatTime(s.startTime)} - ${formatTime(s.endTime)}</td>
        <td>
          <strong>${escapeHtml(s.roomLabel || 'Session')}</strong>
          ${isCoverage ? `<div class="coverage">Coverage for ${escapeHtml(s.scheduledProviderDisplayName || 'Provider')}</div>` : ''}
        </td>
        <td>${isCoverage ? '<span class="badge badge-amber">Coverage</span>' : 'Primary provider'}</td>
        <td>${waitingMap[s.roomCode] || 0} waiting / ${s.roomMaxClients || 12}</td>
        <td><span class="badge ${statusBadge(s.status)}">${escapeHtml(s.status)}</span></td>
        <td class="actions-cell"></td>
      `;
      const actions = document.createElement('div');
      actions.className = 'row-actions';
      const openBtn = document.createElement('button');
      openBtn.className = 'btn btn-primary';
      if (['completed', 'cancelled'].includes(s.status)) {
        openBtn.textContent = 'Completed';
        openBtn.disabled = true;
      } else if (s.status === 'in_progress' || s.status === 'open') {
        openBtn.textContent = 'Enter session';
      } else {
        openBtn.textContent = 'Open session';
      }
      if (!openBtn.disabled) {
        openBtn.addEventListener('click', () => openSession(s));
      }
      actions.appendChild(openBtn);
      const partBtn = document.createElement('button');
      partBtn.className = 'btn';
      partBtn.textContent = 'Participants';
      actions.appendChild(partBtn);
      if (!isCoverage || hasPermission('session.reassign')) {
        const reasBtn = document.createElement('button');
        reasBtn.className = 'btn';
        reasBtn.textContent = 'Reassign';
        reasBtn.addEventListener('click', () => openReassignModal(s));
        actions.appendChild(reasBtn);
      }
      tr.querySelector('.actions-cell').appendChild(actions);
      tbody.appendChild(tr);
    });
    body.innerHTML = '';
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load sessions.');
  }
}

async function countWaitingByRoom(roomCodes) {
  const map = {};
  for (const code of roomCodes) {
    try {
      const data = await api(`/rooms/${code}/waiting`);
      map[code] = (data.waiting || []).length;
    } catch (_e) { map[code] = 0; }
  }
  return map;
}

async function openSession(session) {
  if (['completed', 'cancelled'].includes(session.status)) {
    return;
  }
  try {
    await api(`/sessions/${session.id}/open`, { method: 'POST' });
  } catch (err) {
    // open may fail if already open; continue to session page
  }
  const slug = document.documentElement.dataset.tenantSlug || '';
  const prefix = slug ? `/${slug}` : '';
  const url = new URL(`${prefix}/session/${encodeURIComponent(session.roomCode)}`, window.location.origin);
  url.searchParams.set('sessionId', session.id);
  const currentParams = new URLSearchParams(window.location.search);
  if (currentParams.get('forceRelay') === '1') url.searchParams.set('forceRelay', '1');
  window.location.href = url.toString();
}

function openReassignModal(session) {
  const modal = $('#reassign-modal');
  $('#reassign-session-name').value = `${session.roomLabel || 'Session'} - ${formatTime(session.startTime)}`;
  const select = $('#reassign-provider');
  select.innerHTML = '';
  api('/providers').then((res) => {
    res.providers.forEach((p) => {
      if (p.id === session.actualProviderId) return;
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.display_name || p.user_display_name || p.username;
      select.appendChild(opt);
    });
  });
  $('#reassign-reason').value = '';
  $('#reassign-modal').classList.add('open');
  const submit = $('#reassign-submit');
  submit.onclick = async () => {
    const actualProviderId = select.value;
    const reason = $('#reassign-reason').value;
    try {
      await api(`/sessions/${session.id}/assign`, {
        method: 'PATCH',
        body: { actualProviderId, reason },
      });
      modal.classList.remove('open');
      loadProviderSessions();
    } catch (err) {
      $('#reassign-error').textContent = err.message || 'Reassignment failed.';
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Schedule                                                                   */
/* -------------------------------------------------------------------------- */
function scheduleAddDays(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function scheduleStartOfWeek(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDay();
  const mondayOffset = (day + 6) % 7;
  d.setUTCDate(d.getUTCDate() - mondayOffset);
  return d.toISOString().slice(0, 10);
}

function scheduleStartOfMonth(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

function scheduleAddMonths(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)).toISOString().slice(0, 10);
}

function scheduleDayOfWeek(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`).getUTCDay();
}

function scheduleDayNumber(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`).getUTCDate();
}

function scheduleWeekdayName(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short' });
}

function scheduleIsSameMonth(a, b) {
  const da = new Date(`${a}T00:00:00Z`);
  const db = new Date(`${b}T00:00:00Z`);
  return da.getUTCFullYear() === db.getUTCFullYear() && da.getUTCMonth() === db.getUTCMonth();
}

function scheduleFormatDateLabel(dateStr, view) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (view === 'day') {
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (view === 'week') {
    const start = new Date(d);
    const end = new Date(d);
    end.setUTCDate(end.getUTCDate() + 5);
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function scheduleSortByTime(a, b) {
  return a.startTime.localeCompare(b.startTime);
}

function scheduleModal() {
  let el = $('#schedule-modal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'schedule-modal';
    el.className = 'modal-backdrop';
    el.innerHTML = `
      <div class="modal">
        <div class="modal-header">
          <span id="schedule-modal-title"></span>
          <button class="btn" id="schedule-modal-close">Close</button>
        </div>
        <div class="modal-body" id="schedule-modal-body"></div>
        <div class="modal-footer" id="schedule-modal-footer"></div>
      </div>`;
    document.body.appendChild(el);
    $('#schedule-modal-close').addEventListener('click', () => el.classList.remove('open'));
    el.addEventListener('click', (e) => { if (e.target === el) el.classList.remove('open'); });
  }
  return el;
}

function openScheduleModal(title, body, footer) {
  const modal = scheduleModal();
  $('#schedule-modal-title').textContent = title;
  $('#schedule-modal-body').innerHTML = body;
  $('#schedule-modal-footer').innerHTML = footer;
  modal.classList.add('open');
}

function closeScheduleModal() {
  const modal = $('#schedule-modal');
  if (modal) modal.classList.remove('open');
}

function createScheduleSessionCard(s) {
  const card = document.createElement('div');
  card.className = `schedule-session-card ${s.isCoverage ? 'coverage' : ''} ${s.status === 'cancelled' ? 'cancelled' : ''}`;
  const meta = [
    `${formatTime(s.startTime)} - ${formatTime(s.endTime)}`,
    s.isCoverage
      ? `<span class="coverage">Coverage</span> ${escapeHtml(s.actualProviderDisplayName || '')}`
      : escapeHtml(s.scheduledProviderDisplayName || ''),
  ];
  if (s.participantCount !== undefined) {
    meta.push(`${s.participantCount} / ${s.roomMaxClients || 12}`);
  }
  card.innerHTML = `
    <div class="schedule-session-title">${escapeHtml(s.roomLabel || 'Session')}</div>
    <div class="schedule-session-meta">${meta.join('<br>')}</div>
    <span class="badge ${statusBadge(s.status)}">${escapeHtml(s.status)}</span>
  `;
  card.addEventListener('click', () => openSessionDetailModal(s));
  return card;
}

function renderWeekSchedule(container) {
  const start = state.schedule.start || scheduleStartOfWeek(state.schedule.date);
  const days = [];
  for (let i = 0; i < 6; i += 1) days.push(scheduleAddDays(start, i));
  const byTime = {};
  for (const s of state.schedule.sessions) {
    if (!byTime[s.startTime]) byTime[s.startTime] = {};
    if (!byTime[s.startTime][s.sessionDate]) byTime[s.startTime][s.sessionDate] = [];
    byTime[s.startTime][s.sessionDate].push(s);
  }
  const times = Object.keys(byTime).sort();

  const table = document.createElement('table');
  table.className = 'schedule-table-week';
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.innerHTML = '<th class="time-col">Time</th>';
  days.forEach((day) => {
    const th = document.createElement('th');
    th.textContent = `${scheduleWeekdayName(day)} ${scheduleDayNumber(day)}`;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  if (!times.length) {
    const tr = document.createElement('tr');
    tr.innerHTML = '<td colspan="7" class="empty-state">No sessions this week.</td>';
    tbody.appendChild(tr);
  } else {
    times.forEach((time) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `<td class="time-col">${formatTime(time)}</td>`;
      days.forEach((day) => {
        const td = document.createElement('td');
        td.className = 'schedule-cell';
        const list = (byTime[time] && byTime[time][day]) || [];
        list.forEach((s) => td.appendChild(createScheduleSessionCard(s)));
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  }
  table.appendChild(tbody);
  container.appendChild(table);
}

function renderDaySchedule(container) {
  const sessions = [...state.schedule.sessions].sort(scheduleSortByTime);
  const div = document.createElement('div');
  div.className = 'day-list';
  if (!sessions.length) {
    div.innerHTML = '<div class="day-list-empty">No sessions for this day.</div>';
  } else {
    sessions.forEach((s) => div.appendChild(createScheduleSessionCard(s)));
  }
  container.appendChild(div);
}

function renderMonthSchedule(container) {
  const monthStart = scheduleStartOfMonth(state.schedule.date);
  const startDayOfWeek = scheduleDayOfWeek(monthStart);
  const start = scheduleAddDays(monthStart, -startDayOfWeek);
  const grid = document.createElement('div');
  grid.className = 'month-grid';
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  weekdays.forEach((name) => {
    const header = document.createElement('div');
    header.className = 'month-grid-header';
    header.textContent = name;
    grid.appendChild(header);
  });
  for (let w = 0; w < 6; w += 1) {
    for (let d = 0; d < 7; d += 1) {
      const day = scheduleAddDays(start, w * 7 + d);
      const cell = document.createElement('div');
      cell.className = `month-cell ${!scheduleIsSameMonth(day, monthStart) ? 'schedule-cell-other-month' : ''}`;
      const daySessions = state.schedule.sessions
        .filter((s) => s.sessionDate === day)
        .sort(scheduleSortByTime);
      const inner = document.createElement('div');
      inner.innerHTML = `<div class="day-number">${scheduleDayNumber(day)}</div>`;
      daySessions.forEach((s) => inner.appendChild(createScheduleSessionCard(s)));
      cell.appendChild(inner);
      grid.appendChild(cell);
    }
  }
  container.appendChild(grid);
}

function renderSchedule(container) {
  const canManage = hasPermission('schedule.manage');
  container.innerHTML = `
    <h1 class="page-title">Schedule</h1>
    <p class="page-description">Telehealth Platform session calendar.</p>
    <div class="schedule-toolbar">
      <div class="schedule-toolbar-left">
        <div class="tabs" id="schedule-tabs">
          <button class="tab active" data-view="week">Week</button>
          <button class="tab" data-view="day">Day</button>
          <button class="tab" data-view="month">Month</button>
        </div>
        <button class="btn" id="schedule-prev">&lsaquo;</button>
        <div class="date-label" id="schedule-date-label">-</div>
        <button class="btn" id="schedule-next">&rsaquo;</button>
        <button class="btn" id="schedule-today">Today</button>
      </div>
      <div class="schedule-toolbar-right">
        ${canManage ? '<button class="btn btn-primary" id="schedule-new">+ New session</button>' : ''}
      </div>
    </div>
    <div id="schedule-body" class="schedule-view"></div>
  `;

  state.schedule.view = 'week';
  state.schedule.date = todayStr();

  $$('#schedule-tabs .tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      $$('#schedule-tabs .tab').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      state.schedule.view = btn.dataset.view;
      loadScheduleView(state.schedule.view);
    });
  });
  $('#schedule-prev').addEventListener('click', () => {
    if (state.schedule.view === 'day') state.schedule.date = scheduleAddDays(state.schedule.date, -1);
    else if (state.schedule.view === 'week') state.schedule.date = scheduleAddDays(state.schedule.date, -7);
    else state.schedule.date = scheduleAddMonths(state.schedule.date, -1);
    loadScheduleView(state.schedule.view);
  });
  $('#schedule-next').addEventListener('click', () => {
    if (state.schedule.view === 'day') state.schedule.date = scheduleAddDays(state.schedule.date, 1);
    else if (state.schedule.view === 'week') state.schedule.date = scheduleAddDays(state.schedule.date, 7);
    else state.schedule.date = scheduleAddMonths(state.schedule.date, 1);
    loadScheduleView(state.schedule.view);
  });
  $('#schedule-today').addEventListener('click', () => {
    state.schedule.date = todayStr();
    loadScheduleView(state.schedule.view);
  });
  if (canManage) {
    $('#schedule-new').addEventListener('click', () => openCreateSessionModal());
  }

  loadScheduleView('week');
}

async function loadScheduleView(view) {
  const body = $('#schedule-body');
  body.innerHTML = '<p class="empty-state">Loading schedule...</p>';
  $('#schedule-date-label').textContent = scheduleFormatDateLabel(state.schedule.date, view);

  const providerOnly = isProvider() && !hasPermission('organization.manage');
  const endpoint = providerOnly
    ? `/sessions/schedule?date=${state.schedule.date}&view=${view}`
    : `/admin/organizations/${state.orgId}/sessions?date=${state.schedule.date}&view=${view}`;

  try {
    const res = await api(endpoint);
    state.schedule.sessions = res.sessions || [];
    state.schedule.start = res.start;
    state.schedule.end = res.end;

    body.innerHTML = '';
    if (view === 'week') renderWeekSchedule(body);
    else if (view === 'day') renderDaySchedule(body);
    else renderMonthSchedule(body);
  } catch (err) {
    body.innerHTML = `<p class="error-text">Unable to load schedule: ${escapeHtml(err.message || '')}</p>`;
  }
}

async function openCreateSessionModal() {
  try {
    const res = await api(`/admin/organizations/${state.orgId}/schedule-resources`);
    const rooms = res.rooms || [];
    const providers = res.providers || [];
    const roomOptions = rooms.map((r) => `<option value="${r.id}">${escapeHtml(r.label || r.room_code || 'Room')}</option>`).join('');
    const providerOptions = providers.map((p) => `<option value="${p.id}">${escapeHtml(p.display_name || p.username || 'Provider')}</option>`).join('');

    openScheduleModal(
      'New session',
      `
        <div class="tabs" id="create-tabs">
          <button class="tab active" data-type="one-time">One-time</button>
          <button class="tab" data-type="recurring">Recurring series</button>
        </div>
        <div class="tab-page active" id="page-one-time">
          <div class="field-grid">
            <div class="field"><label>Room / group</label><select id="create-room">${roomOptions}</select></div>
            <div class="field"><label>Scheduled provider</label><select id="create-provider">${providerOptions}</select></div>
            <div class="field"><label>Date</label><input type="date" id="create-date" value="${todayStr()}" /></div>
            <div class="field"><label>Start time</label><input type="time" id="create-start" value="09:00" /></div>
            <div class="field"><label>End time</label><input type="time" id="create-end" value="12:00" /></div>
            <div class="field"><label>Capacity override</label><input type="number" id="create-capacity" placeholder="Room default" /></div>
          </div>
        </div>
        <div class="tab-page" id="page-recurring">
          <div class="field-grid">
            <div class="field"><label>Room / group</label><select id="rec-room">${roomOptions}</select></div>
            <div class="field"><label>Scheduled provider</label><select id="rec-provider">${providerOptions}</select></div>
            <div class="field"><label>Recurrence days</label>
              <div class="recurrence-days field-grid" id="rec-days" style="grid-template-columns: repeat(3, 1fr); gap: 8px;">
                ${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name, i) => `<label><input type="checkbox" value="${i + 1}" /> ${name}</label>`).join('')}
              </div>
            </div>
            <div class="field"><label>Start time</label><input type="time" id="rec-start" value="09:00" /></div>
            <div class="field"><label>End time</label><input type="time" id="rec-end" value="12:00" /></div>
            <div class="field"><label>Effective start</label><input type="date" id="rec-start-date" value="${todayStr()}" /></div>
            <div class="field"><label>Effective end (optional)</label><input type="date" id="rec-end-date" /></div>
            <div class="field"><label>Capacity override</label><input type="number" id="rec-capacity" placeholder="Room default" /></div>
          </div>
        </div>
        <div id="create-error" class="error-text"></div>
      `,
      '<button class="btn" id="create-cancel">Cancel</button><button class="btn btn-primary" id="create-save">Create</button>'
    );

    $$('#create-tabs .tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        $$('#create-tabs .tab').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        $$('.tab-page').forEach((p) => p.classList.remove('active'));
        $(`#page-${btn.dataset.type}`).classList.add('active');
      });
    });

    $('#create-cancel').addEventListener('click', closeScheduleModal);
    $('#create-save').addEventListener('click', async () => {
      try {
        const activeTab = $('#create-tabs .tab.active').dataset.type;
        if (activeTab === 'one-time') {
          await api('/sessions', {
            method: 'POST',
            body: {
              providerRoomId: $('#create-room').value,
              scheduledProviderId: $('#create-provider').value,
              sessionDate: $('#create-date').value,
              startTime: $('#create-start').value,
              endTime: $('#create-end').value,
              capacityOverride: $('#create-capacity').value ? parseInt($('#create-capacity').value, 10) : null,
            },
          });
        } else {
          const days = [...$$('#rec-days input:checked')].map((cb) => parseInt(cb.value, 10));
          await api('/sessions/series', {
            method: 'POST',
            body: {
              providerRoomId: $('#rec-room').value,
              scheduledProviderId: $('#rec-provider').value,
              recurrenceDays: days,
              startTime: $('#rec-start').value,
              endTime: $('#rec-end').value,
              effectiveStart: $('#rec-start-date').value,
              effectiveEnd: $('#rec-end-date').value || null,
              capacityOverride: $('#rec-capacity').value ? parseInt($('#rec-capacity').value, 10) : null,
            },
          });
        }
        closeScheduleModal();
        loadScheduleView(state.schedule.view);
      } catch (err) {
        $('#create-error').textContent = err.message || 'Create failed.';
      }
    });
  } catch (err) {
    openScheduleModal('Error', `<p class="error-text">${escapeHtml(err.message || 'Unable to load resources.')}</p>`, '<button class="btn" id="err-close">Close</button>');
    $('#err-close').addEventListener('click', closeScheduleModal);
  }
}

async function openSessionDetailModal(s) {
  const canManage = hasPermission('schedule.manage');
  const isProviderUser = isProvider() && state.providerId;
  const isOwnSession = isProviderUser && (s.scheduledProviderId === state.providerId || s.actualProviderId === state.providerId);
  const actions = [];
  if (canManage) {
    actions.push('<button class="btn btn-primary" id="session-edit-btn">Edit</button>');
    if (s.status !== 'cancelled') actions.push('<button class="btn btn-danger" id="session-cancel-btn">Cancel</button>');
  }
  if (isProviderUser && isOwnSession && s.status !== 'cancelled') {
    actions.push('<button class="btn" id="session-reassign-btn">Reassign</button>');
  }
  if (s.status === 'open' || s.status === 'in_progress') {
    const slug = document.documentElement.dataset.tenantSlug || '';
    const prefix = slug ? `/${slug}` : '';
    actions.push(`<a class="btn btn-primary" href="${prefix}/session/${encodeURIComponent(s.roomCode)}?sessionId=${encodeURIComponent(s.id)}">Enter session</a>`);
  }

  const coverageNote = s.isCoverage
    ? `<div class="notice"><strong>Coverage assignment.</strong> Scheduled provider is ${escapeHtml(s.scheduledProviderDisplayName || '—')}.</div>`
    : '';
  const seriesNote = s.seriesId
    ? `<div class="notice">This occurrence is part of a recurring series. Edits can apply to this occurrence, this and future occurrences, or the entire series.</div>`
    : '';

  openScheduleModal(
    escapeHtml(s.roomLabel || 'Session'),
    `
      <div class="detail-grid" style="grid-template-columns: 1fr 1fr;">
        <div><div class="detail-label">Date</div><div class="detail-value">${formatDate(s.sessionDate)}</div></div>
        <div><div class="detail-label">Time</div><div class="detail-value">${formatTime(s.startTime)} - ${formatTime(s.endTime)}</div></div>
        <div><div class="detail-label">Scheduled provider</div><div class="detail-value">${escapeHtml(s.scheduledProviderDisplayName || '—')}</div></div>
        <div><div class="detail-label">Actual provider</div><div class="detail-value">${escapeHtml(s.actualProviderDisplayName || '—')}</div></div>
        <div><div class="detail-label">Participants</div><div class="detail-value">${s.participantCount || 0} / ${s.roomMaxClients || 12}</div></div>
        <div><div class="detail-label">Status</div><div class="detail-value">${escapeHtml(s.status)}</div></div>
      </div>
      ${coverageNote}
      ${seriesNote}
    `,
    actions.join(' ') || '<button class="btn" id="schedule-modal-close-action">Close</button>'
  );

  const editBtn = $('#session-edit-btn');
  if (editBtn) editBtn.addEventListener('click', () => openEditSessionModal(s));
  const cancelBtn = $('#session-cancel-btn');
  if (cancelBtn) cancelBtn.addEventListener('click', () => openCancelSessionModal(s));
  const reassignBtn = $('#session-reassign-btn');
  if (reassignBtn) reassignBtn.addEventListener('click', () => openReassignSessionModal(s));
  const closeAction = $('#schedule-modal-close-action');
  if (closeAction) closeAction.addEventListener('click', closeScheduleModal);
}

async function openEditSessionModal(s) {
  try {
    const res = await api(`/admin/organizations/${state.orgId}/schedule-resources`);
    const rooms = res.rooms || [];
    const providers = res.providers || [];
    const isRecurring = !!s.seriesId;
    const scopeOptions = isRecurring
      ? `<div class="field"><label>Apply to</label><select id="edit-scope"><option value="occurrence">This occurrence</option><option value="future">This and future occurrences</option><option value="series">Entire series</option></select></div>`
      : '';
    const roomOptions = rooms.map((r) => `<option value="${r.id}" ${r.id === s.providerRoomId ? 'selected' : ''}>${escapeHtml(r.label || r.room_code || 'Room')}</option>`).join('');
    const providerOptions = providers.map((p) => `<option value="${p.id}" ${p.id === s.scheduledProviderId ? 'selected' : ''}>${escapeHtml(p.display_name || p.username || 'Provider')}</option>`).join('');
    const actualProviderOptions = providers.map((p) => `<option value="${p.id}" ${p.id === s.actualProviderId ? 'selected' : ''}>${escapeHtml(p.display_name || p.username || 'Provider')}</option>`).join('');

    openScheduleModal(
      isRecurring ? 'Edit occurrence' : 'Edit session',
      `
        <div class="notice">Leave a field unchanged by keeping its current value.</div>
        ${scopeOptions}
        <div class="field-grid">
          <div class="field"><label>Room / group</label><select id="edit-room">${roomOptions}</select></div>
          <div class="field"><label>Scheduled provider</label><select id="edit-scheduled-provider">${providerOptions}</select></div>
          <div class="field"><label>Actual provider (coverage)</label><select id="edit-actual-provider"><option value="">— same as scheduled —</option>${actualProviderOptions}</select></div>
          <div class="field"><label>Capacity override</label><input type="number" id="edit-capacity" value="${s.capacityOverride || ''}" /></div>
          <div class="field"><label>Start time</label><input type="time" id="edit-start" value="${s.startTime}" /></div>
          <div class="field"><label>End time</label><input type="time" id="edit-end" value="${s.endTime}" /></div>
        </div>
        <div id="edit-error" class="error-text"></div>
      `,
      '<button class="btn" id="edit-cancel">Cancel</button><button class="btn btn-primary" id="edit-save">Save changes</button>'
    );

    $('#edit-cancel').addEventListener('click', closeScheduleModal);
    $('#edit-save').addEventListener('click', async () => {
      try {
        const fields = {};
        const roomSelect = $('#edit-room');
        if (roomSelect && roomSelect.value !== s.providerRoomId) fields.providerRoomId = roomSelect.value;
        const schedSelect = $('#edit-scheduled-provider');
        if (schedSelect && schedSelect.value !== s.scheduledProviderId) fields.scheduledProviderId = schedSelect.value;
        const actualSelect = $('#edit-actual-provider');
        if (actualSelect && actualSelect.value && actualSelect.value !== s.actualProviderId) fields.actualProviderId = actualSelect.value;
        const cap = $('#edit-capacity');
        if (cap && (cap.value || '') !== String(s.capacityOverride || '')) fields.capacityOverride = cap.value ? parseInt(cap.value, 10) : null;
        const start = $('#edit-start');
        if (start && start.value !== s.startTime) fields.startTime = start.value;
        const end = $('#edit-end');
        if (end && end.value !== s.endTime) fields.endTime = end.value;

        const scope = isRecurring ? ($('#edit-scope')?.value || 'occurrence') : 'occurrence';
        await api(`/sessions/${s.id}`, { method: 'PATCH', body: { scope, ...fields } });
        closeScheduleModal();
        loadScheduleView(state.schedule.view);
      } catch (err) {
        $('#edit-error').textContent = err.message || 'Update failed.';
      }
    });
  } catch (err) {
    openScheduleModal('Error', `<p class="error-text">${escapeHtml(err.message || 'Unable to load resources.')}</p>`, '<button class="btn" id="err-close">Close</button>');
    $('#err-close').addEventListener('click', closeScheduleModal);
  }
}

function openCancelSessionModal(s) {
  const isRecurring = !!s.seriesId;
  openScheduleModal(
    'Cancel session',
    `
      <p>Cancel this session${isRecurring ? ' occurrence' : ''}?</p>
      ${isRecurring ? `
        <div class="field">
          <label>Apply cancellation to</label>
          <select id="cancel-scope">
            <option value="occurrence">This occurrence</option>
            <option value="future">This and future occurrences</option>
            <option value="series">Entire series</option>
          </select>
        </div>
      ` : ''}
      <div id="cancel-error" class="error-text"></div>
    `,
    '<button class="btn" id="cancel-cancel">Close</button><button class="btn btn-danger" id="cancel-confirm">Cancel session</button>'
  );
  $('#cancel-cancel').addEventListener('click', closeScheduleModal);
  $('#cancel-confirm').addEventListener('click', async () => {
    try {
      const scope = isRecurring ? ($('#cancel-scope')?.value || 'occurrence') : 'occurrence';
      await api(`/sessions/${s.id}/cancel`, { method: 'POST', body: { scope } });
      closeScheduleModal();
      loadScheduleView(state.schedule.view);
    } catch (err) {
      $('#cancel-error').textContent = err.message || 'Cancellation failed.';
    }
  });
}

async function openReassignSessionModal(s) {
  try {
    const res = await api('/sessions/providers');
    const providers = (res.providers || []).filter((p) => p.id !== state.providerId);
    const options = providers.map((p) => `<option value="${p.id}">${escapeHtml(p.display_name || p.user_display_name || p.username || 'Provider')}</option>`).join('');
    openScheduleModal(
      'Reassign session',
      `
        <div class="notice">Reassign your session occurrence to another provider. The originally scheduled provider remains recorded.</div>
        <div class="field"><label>New provider</label><select id="reassign-provider">${options}</select></div>
        <div class="field"><label>Reason</label><select id="reassign-reason"><option>Sick</option><option>PTO</option><option>Schedule conflict</option><option>Emergency</option><option>Other</option></select></div>
        <div id="reassign-error" class="error-text"></div>
      `,
      '<button class="btn" id="reassign-cancel">Cancel</button><button class="btn btn-primary" id="reassign-save">Reassign</button>'
    );
    $('#reassign-cancel').addEventListener('click', closeScheduleModal);
    $('#reassign-save').addEventListener('click', async () => {
      try {
        await api(`/sessions/${s.id}/assign`, {
          method: 'PATCH',
          body: {
            actualProviderId: $('#reassign-provider').value,
            reason: $('#reassign-reason').value,
          },
        });
        closeScheduleModal();
        loadScheduleView(state.schedule.view);
      } catch (err) {
        $('#reassign-error').textContent = err.message || 'Reassignment failed.';
      }
    });
  } catch (err) {
    openScheduleModal('Error', `<p class="error-text">${escapeHtml(err.message || 'Unable to load resources.')}</p>`, '<button class="btn" id="err-close">Close</button>');
    $('#err-close').addEventListener('click', closeScheduleModal);
  }
}

function statusBadge(status) {
  if (status === 'completed' || status === 'cancelled') return 'badge-gray';
  if (status === 'open' || status === 'in_progress') return 'badge-green';
  if (status === 'scheduled') return 'badge-blue';
  return 'badge-gray';
}

/* -------------------------------------------------------------------------- */
/* Clients                                                                    */
/* -------------------------------------------------------------------------- */
async function renderClients(container) {
  container.innerHTML = `
    <h1 class="page-title">Clients / Participants</h1>
    <p class="page-description">Operational Telehealth Platform access records for this organization.</p>
    <div class="toolbar">
      <div class="toolbar-left"><input type="text" class="search" placeholder="Search clients..." id="client-search"/></div>
      <div class="toolbar-right"><button class="btn btn-primary" id="add-client-btn">Add client</button></div>
    </div>
    <div id="clients-body"></div>
  `;
  $('#add-client-btn').addEventListener('click', () => alert('Client creation UI not implemented in this milestone.'));
  $('#client-search').addEventListener('input', () => loadClients());
  await loadClients();
}

async function loadClients() {
  const body = $('#clients-body');
  const q = ($('#client-search')?.value || '').toLowerCase();
  try {
    const res = await api(`/admin/organizations/${state.orgId}/clients`);
    const clients = (res.clients || []).filter((c) =>
      (c.display_name || '').toLowerCase().includes(q) || (c.username || '').toLowerCase().includes(q)
    );
    if (!clients.length) {
      body.innerHTML = '<p class="empty-state">No clients found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.className = 'admin-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>Client</th>
          <th>Portal</th>
          <th>Group</th>
          <th>Provider</th>
          <th>Next session</th>
          <th></th>
        </tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    clients.forEach((c) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>
          <div class="name">${escapeHtml(c.display_name || '—')}</div>
          <div class="subtext">${escapeHtml(c.username || '')}</div>
        </td>
        <td><span class="badge ${c.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(c.status)}</span></td>
        <td>${escapeHtml(c.roomLabel || '—')}</td>
        <td>${escapeHtml(c.providerDisplayName ? `${c.providerDisplayName}${c.providerStatus !== 'active' ? ' (inactive)' : ''}` : '—')}</td>
        <td>${escapeHtml(c.nextSessionDate ? formatDate(c.nextSessionDate) : '—')}</td>
        <td style="text-align:right"><button class="btn" id="manage-client-${c.id}">Manage</button></td>
      `;
      tbody.appendChild(tr);
      const btn = tr.querySelector(`#manage-client-${c.id}`);
      if (btn) btn.addEventListener('click', () => openClientDetailModal(c.id));
    });
    body.innerHTML = '';
    body.appendChild(table);
  } catch (err) {
    console.error('loadClients failed:', err);
    body.innerHTML = '<p class="error-text">Unable to load clients.</p>';
  }
}

async function openClientDetailModal(clientId) {
  try {
    const res = await api(`/admin/organizations/${state.orgId}/clients/${clientId}`);
    const c = res.client;
    const active = c.activeAuthorizations[0];
    const currentRoom = active || {};

    const activeRows = c.activeAuthorizations.map((a) => `
      <tr>
        <td>${escapeHtml(a.roomLabel || '—')}</td>
        <td>${escapeHtml(a.providerDisplayName ? `${a.providerDisplayName}${a.providerStatus !== 'active' ? ' (inactive)' : ''}` : '—')}</td>
        <td>${escapeHtml(a.effectiveStart ? formatDate(a.effectiveStart) : '—')}</td>
        <td><span class="badge badge-green">${escapeHtml(a.status)}</span></td>
        <td>
          <button class="btn" id="reassign-auth-${a.id}" ${a.providerStatus !== 'active' ? 'disabled' : ''}>Reassign</button>
          <button class="btn btn-danger" id="remove-auth-${a.id}" ${a.providerStatus !== 'active' ? 'disabled' : ''}>Remove</button>
        </td>
      </tr>
    `).join('');

    const historyRows = c.history.length
      ? c.history.map((h) => `
        <tr>
          <td>${escapeHtml(h.roomLabel || '—')}</td>
          <td>${escapeHtml(h.providerDisplayName || '—')}</td>
          <td>${escapeHtml(h.effectiveStart ? formatDate(h.effectiveStart) : '—')}</td>
          <td>${escapeHtml(h.effectiveEnd ? formatDate(h.effectiveEnd) : '—')}</td>
          <td><span class="badge ${h.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(h.status)}</span></td>
        </tr>
      `).join('')
      : '<tr><td colspan="5" class="empty-state-cell">No membership history.</td></tr>';

    openModal('client-detail-modal', `
      <div class="modal-header"><strong>${escapeHtml(c.display_name || c.username || 'Client')} </strong><button class="btn" id="close-client-detail">Close</button></div>
      <div class="modal-body">
        <div class="detail-top admin-detail-top">
          <div class="detail-item"><div class="detail-label">Portal status</div><div class="detail-value">${escapeHtml(c.status)}</div></div>
          <div class="detail-item"><div class="detail-label">Primary group</div><div class="detail-value">${escapeHtml(currentRoom.roomLabel || '—')}</div></div>
          <div class="detail-item"><div class="detail-label">Current provider</div><div class="detail-value">${escapeHtml(currentRoom.providerDisplayName ? `${currentRoom.providerDisplayName}${currentRoom.providerStatus !== 'active' ? ' (inactive)' : ''}` : '—')}</div></div>
          <div class="detail-item"><div class="detail-label">Effective start</div><div class="detail-value">${escapeHtml(currentRoom.effectiveStart ? formatDate(currentRoom.effectiveStart) : '—')}</div></div>
        </div>
        <div class="card">
          <div class="card-header">Active group assignments <button class="btn btn-primary" id="client-add-group">+ Add group</button></div>
          <div class="card-body">
            <table class="admin-table"><thead><tr><th>Group</th><th>Provider</th><th>Effective</th><th>Status</th><th></th></tr></thead><tbody>${activeRows}</tbody></table>
          </div>
        </div>
        <div class="card">
          <div class="card-header">Membership history</div>
          <div class="card-body">
            <table class="admin-table"><thead><tr><th>Group</th><th>Provider</th><th>From</th><th>To</th><th>Status</th></tr></thead><tbody>${historyRows}</tbody></table>
          </div>
        </div>
      </div>
      <div class="modal-footer"><button class="btn" id="cancel-client-detail">Close</button></div>
    `);

    $('#close-client-detail')?.addEventListener('click', () => closeModal('client-detail-modal'));
    $('#cancel-client-detail')?.addEventListener('click', () => closeModal('client-detail-modal'));
    $('#client-add-group')?.addEventListener('click', () => openAdminAddModal(clientId, c.display_name || c.username));
    c.activeAuthorizations.forEach((a) => {
      $(`#reassign-auth-${a.id}`)?.addEventListener('click', () => openAdminReassignModal(clientId, a.roomId, a.roomLabel, c.display_name || c.username, a.providerDisplayName));
      $(`#remove-auth-${a.id}`)?.addEventListener('click', () => openAdminRemoveModal(clientId, a.roomId, a.roomLabel, c.display_name || c.username));
    });
  } catch (err) {
    console.error('openClientDetailModal failed:', err);
    setError('Unable to load client detail.');
  }
}

async function openAdminAddModal(clientId, clientName) {
  try {
    const { targets } = await api('/provider/groups/reassignment-targets');
    const choices = targets.filter((t) => t.roomId);
    if (!choices.length) { setError('No active groups are available for assignment.'); return; }
    const options = choices.map((t) => `<option value="${t.roomId}">${escapeHtml(t.label)} — ${escapeHtml(t.providerDisplayName)}</option>`).join('');
    openModal('admin-add-modal', `
      <div class="modal-header"><strong>Add to group</strong><button class="btn" id="close-admin-add">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Participant</label><input value="${escapeHtml(clientName)}" disabled></div>
        <div class="field"><label>Group</label><select id="admin-add-room">${options}</select></div>
        <div class="error-text" id="admin-add-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-admin-add">Cancel</button>
        <button class="btn btn-primary" id="submit-admin-add">Add to group</button>
      </div>
    `);
    $('#close-admin-add')?.addEventListener('click', () => closeModal('admin-add-modal'));
    $('#cancel-admin-add')?.addEventListener('click', () => closeModal('admin-add-modal'));
    $('#submit-admin-add')?.addEventListener('click', async () => {
      const roomId = $('#admin-add-room').value;
      try {
        await api(`/provider/groups/${roomId}/participants`, { method: 'POST', body: { clientId } });
        closeModal('admin-add-modal');
        closeModal('client-detail-modal');
        loadClients();
      } catch (err) {
        $('#admin-add-error').textContent = err.message || 'Add failed.';
      }
    });
  } catch (err) {
    console.error('openAdminAddModal failed:', err);
    setError('Unable to load groups for assignment.');
  }
}

async function openAdminRejoinModal(clientId, clientName) {
  try {
    const { targets } = await api('/provider/groups/reassignment-targets');
    const choices = targets.filter((t) => t.roomId);
    if (!choices.length) { setError('No active groups are available for rejoin.'); return; }
    const options = choices.map((t) => `<option value="${t.roomId}">${escapeHtml(t.label)} — ${escapeHtml(t.providerDisplayName)}</option>`).join('');
    openModal('admin-rejoin-modal', `
      <div class="modal-header"><strong>Rejoin group</strong><button class="btn" id="close-admin-rejoin">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Participant</label><input value="${escapeHtml(clientName)}" disabled></div>
        <div class="field"><label>Group</label><select id="admin-rejoin-room">${options}</select></div>
        <div class="error-text" id="admin-rejoin-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-admin-rejoin">Cancel</button>
        <button class="btn btn-primary" id="submit-admin-rejoin">Rejoin group</button>
      </div>
    `);
    $('#close-admin-rejoin')?.addEventListener('click', () => closeModal('admin-rejoin-modal'));
    $('#cancel-admin-rejoin')?.addEventListener('click', () => closeModal('admin-rejoin-modal'));
    $('#submit-admin-rejoin')?.addEventListener('click', async () => {
      const roomId = $('#admin-rejoin-room').value;
      try {
        await api(`/provider/groups/${roomId}/participants/${clientId}/rejoin`, { method: 'POST' });
        closeModal('admin-rejoin-modal');
        closeModal('client-detail-modal');
        loadClients();
      } catch (err) {
        $('#admin-rejoin-error').textContent = err.message || 'Rejoin failed.';
      }
    });
  } catch (err) {
    console.error('openAdminRejoinModal failed:', err);
    setError('Unable to load groups for rejoin.');
  }
}

async function openAdminRemoveModal(clientId, roomId, roomLabel, clientName) {
  openModal('admin-remove-modal', `
    <div class="modal-header"><strong>Remove from group</strong><button class="btn" id="close-admin-remove">Close</button></div>
    <div class="modal-body">
      <p>Remove <strong>${escapeHtml(clientName)}</strong> from <strong>${escapeHtml(roomLabel)}</strong>?</p>
      <div class="note">This is a Telehealth group removal, not a clinical discharge.</div>
      <div class="error-text" id="admin-remove-error"></div>
    </div>
    <div class="modal-footer">
      <button class="btn" id="cancel-admin-remove">Cancel</button>
      <button class="btn btn-danger" id="submit-admin-remove">Remove from group</button>
    </div>
  `);
  $('#close-admin-remove')?.addEventListener('click', () => closeModal('admin-remove-modal'));
  $('#cancel-admin-remove')?.addEventListener('click', () => closeModal('admin-remove-modal'));
  $('#submit-admin-remove')?.addEventListener('click', async () => {
    try {
      await api(`/provider/groups/${roomId}/participants/${clientId}`, { method: 'PATCH' });
      closeModal('admin-remove-modal');
      closeModal('client-detail-modal');
      loadClients();
    } catch (err) {
      $('#admin-remove-error').textContent = err.message || 'Remove failed.';
    }
  });
}

async function openAdminReassignModal(clientId, roomId, roomLabel, clientName, currentProviderDisplayName) {
  try {
    const { targets } = await api('/provider/groups/reassignment-targets');
    const choices = targets.filter((t) => t.roomId !== roomId);
    if (!choices.length) { setError('No valid target groups are available.'); return; }
    const options = choices.map((t) => `<option value="${t.roomId}">${escapeHtml(t.label)} — ${escapeHtml(t.providerDisplayName)}</option>`).join('');
    openModal('admin-reassign-modal', `
      <div class="modal-header"><strong>Reassign participant</strong><button class="btn" id="close-admin-reassign">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Participant</label><input value="${escapeHtml(clientName)}" disabled></div>
        <div class="field"><label>Current group</label><input value="${escapeHtml(roomLabel)} — ${escapeHtml(currentProviderDisplayName || '')}" disabled></div>
        <div class="field"><label>Target group</label><select id="admin-reassign-room">${options}</select></div>
        <div class="error-text" id="admin-reassign-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-admin-reassign">Cancel</button>
        <button class="btn btn-primary" id="submit-admin-reassign">Reassign</button>
      </div>
    `);
    $('#close-admin-reassign')?.addEventListener('click', () => closeModal('admin-reassign-modal'));
    $('#cancel-admin-reassign')?.addEventListener('click', () => closeModal('admin-reassign-modal'));
    $('#submit-admin-reassign')?.addEventListener('click', async () => {
      const targetRoomId = $('#admin-reassign-room').value;
      try {
        await api(`/participants/${clientId}/provider-room`, { method: 'PATCH', body: { providerRoomId: targetRoomId } });
        closeModal('admin-reassign-modal');
        closeModal('client-detail-modal');
        loadClients();
      } catch (err) {
        $('#admin-reassign-error').textContent = err.message || 'Reassignment failed.';
      }
    });
  } catch (err) {
    console.error('openAdminReassignModal failed:', err);
    setError('Unable to load reassignment targets.');
  }
}

/* -------------------------------------------------------------------------- */
/* Providers                                                                  */
/* -------------------------------------------------------------------------- */
async function renderProviders(container) {
  container.innerHTML = `
    <h1 class="page-title">Providers</h1>
    <p class="page-description">Provider profiles and assigned participants in this organization.</p>
    <div id="providers-body"></div>
  `;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/providers`);
    const providers = res.providers || [];
    const body = $('#providers-body');
    if (!providers.length) {
      body.innerHTML = '<p class="empty-state">No providers found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.className = 'admin-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>Provider</th>
          <th>Status</th>
          <th>Groups</th>
          <th>Participants</th>
          <th></th>
        </tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    providers.forEach((p) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>
          <div class="name">${escapeHtml(p.display_name || '—')}</div>
          <div class="subtext">${escapeHtml(p.username || '')}</div>
        </td>
        <td><span class="badge ${p.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(p.status)}</span></td>
        <td>—</td>
        <td>—</td>
        <td style="text-align:right"><button class="btn" id="manage-provider-${p.id}">Manage</button></td>
      `;
      tbody.appendChild(tr);
      const btn = tr.querySelector(`#manage-provider-${p.id}`);
      if (btn) btn.addEventListener('click', () => openProviderDetailModal(p.id));
    });
    body.innerHTML = '';
    body.appendChild(table);
  } catch (err) {
    console.error('renderProviders failed:', err);
    setError('Unable to load providers.');
  }
}

async function openProviderDetailModal(providerId) {
  try {
    const res = await api(`/admin/organizations/${state.orgId}/providers/${providerId}`);
    const p = res.provider;
    const hasInactiveWithParticipants = p.status !== 'active' && p.participants.length > 0;

    const roomRows = p.rooms.map((r) => `
      <tr>
        <td>${escapeHtml(r.label || '—')}</td>
        <td>${escapeHtml(r.roomCode || '—')}</td>
        <td><span class="badge ${r.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(r.status)}</span></td>
        <td>${r.activeParticipantCount}</td>
      </tr>
    `).join('');

    const participantRows = p.participants.map((part) => `
      <tr>
        <td>
          <div class="name">${escapeHtml(part.displayName || '—')}</div>
          <div class="subtext">${escapeHtml(part.username || '')}</div>
        </td>
        <td>${escapeHtml(part.roomLabel || '—')}</td>
        <td>${escapeHtml(part.effectiveStart ? formatDate(part.effectiveStart) : '—')}</td>
        <td style="text-align:right">
          <button class="btn" id="provider-client-manage-${part.id}">Manage</button>
        </td>
      </tr>
    `).join('');

    openModal('provider-detail-modal', `
      <div class="modal-header"><strong>${escapeHtml(p.displayName || p.username || 'Provider')} </strong><button class="btn" id="close-provider-detail">Close</button></div>
      <div class="modal-body">
        <div class="detail-top admin-detail-top">
          <div class="detail-item"><div class="detail-label">Provider status</div><div class="detail-value"><span class="badge ${p.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(p.status)}</span></div></div>
          <div class="detail-item"><div class="detail-label">Assigned groups</div><div class="detail-value">${p.rooms.length}</div></div>
          <div class="detail-item"><div class="detail-label">Current participants</div><div class="detail-value">${p.participants.length}</div></div>
          <div class="detail-item"><div class="detail-label">Username</div><div class="detail-value">${escapeHtml(p.username || '—')}</div></div>
        </div>
        ${hasInactiveWithParticipants ? '<div class="warning" id="provider-inactive-warning">This provider is inactive but still has active participant assignments. Reassign or remove these participants to clear the warning.</div>' : ''}
        <div class="card">
          <div class="card-header">Groups</div>
          <div class="card-body">
            <table class="admin-table"><thead><tr><th>Group</th><th>Code</th><th>Status</th><th>Participants</th></tr></thead><tbody>${roomRows || '<tr><td colspan="4" class="empty-state-cell">No groups.</td></tr>'}</tbody></table>
          </div>
        </div>
        <div class="card">
          <div class="card-header">Assigned participants</div>
          <div class="card-body">
            <table class="admin-table"><thead><tr><th>Participant</th><th>Group</th><th>Effective</th><th></th></tr></thead><tbody>${participantRows || '<tr><td colspan="4" class="empty-state-cell">No active participants.</td></tr>'}</tbody></table>
          </div>
        </div>
      </div>
      <div class="modal-footer"><button class="btn" id="cancel-provider-detail">Close</button></div>
    `);

    $('#close-provider-detail')?.addEventListener('click', () => closeModal('provider-detail-modal'));
    $('#cancel-provider-detail')?.addEventListener('click', () => closeModal('provider-detail-modal'));
    p.participants.forEach((part) => {
      $(`#provider-client-manage-${part.id}`)?.addEventListener('click', () => openClientDetailModal(part.id));
    });
  } catch (err) {
    console.error('openProviderDetailModal failed:', err);
    setError('Unable to load provider detail.');
  }
}


/* -------------------------------------------------------------------------- */
/* Users / Roles / Supervisors / Audit / Acknowledgments / Settings           */
/* -------------------------------------------------------------------------- */
async function renderUsers(container) {
  container.innerHTML = `
    <h1 class="page-title">Users</h1>
    <p class="page-description">Staff users and organization memberships.</p>
    <div id="users-body"></div>
  `;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/memberships`);
    const memberships = res.memberships || [];
    const body = $('#users-body');
    if (!memberships.length) {
      body.innerHTML = '<p class="empty-state">No memberships found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>User</th><th>Status</th><th>Roles</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    memberships.forEach((m) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${escapeHtml(m.display_name || m.username)}</td>
        <td><span class="badge ${m.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(m.status)}</span></td>
        <td>${(m.roles || []).map((r) => escapeHtml(r.name)).join(', ') || '—'}</td>
      `;
      tbody.appendChild(tr);
    });
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load users.');
  }
}

async function renderSupervisors(container) {
  container.innerHTML = `
    <h1 class="page-title">Supervisors</h1>
    <p class="page-description">Clinical supervisor assignments.</p>
    <button class="btn btn-primary" id="add-supervisor-btn">+ Add supervisor assignment</button>
    <div id="supervisors-body" class="panel" style="margin-top: 1rem;"></div>
  `;
  $('#add-supervisor-btn')?.addEventListener('click', () => openSupervisorModal());
  await loadSupervisorsTable();
}

async function loadSupervisorsTable() {
  const body = $('#supervisors-body');
  if (!body) return;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/supervisors`);
    const assignments = res.assignments || [];
    if (!assignments.length) {
      body.innerHTML = '<p class="empty-state">No supervisor assignments found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>Supervisor</th><th>Supervisee</th><th>Type</th><th>Status</th><th>Effective</th><th>End</th><th></th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    assignments.forEach((a) => {
      const tr = document.createElement('tr');
      const supervisorName = a.supervisor_display_name || a.supervisor_username;
      const typeLabel = a.external ? 'External non-provider' : 'Employee/provider';
      const typeClass = a.external ? 'badge-amber' : 'badge-blue';
      tr.innerHTML = `
        <td>${escapeHtml(supervisorName)}</td>
        <td>${escapeHtml(a.supervisee_display_name || a.supervisee_username)}</td>
        <td><span class="badge ${typeClass}">${escapeHtml(typeLabel)}</span></td>
        <td><span class="badge ${a.active ? 'badge-green' : 'badge-gray'}">${a.active ? 'Active' : 'Inactive'}</span></td>
        <td>${formatAssignmentDate(a.effective_start) || '—'}</td>
        <td>${formatAssignmentDate(a.effective_end) || '—'}</td>
        <td><button class="btn btn-sm" data-id="${escapeHtml(a.id)}">Manage</button></td>
      `;
      tr.querySelector('button').addEventListener('click', () => openSupervisorModal(a));
      tbody.appendChild(tr);
    });
    body.innerHTML = '';
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load supervisors.');
  }
}

async function openSupervisorModal(assignment = null) {
  const isEdit = Boolean(assignment);
  const [candidatesRes, providersRes] = await Promise.all([
    api(`/admin/organizations/${state.orgId}/supervisors/candidates`),
    api(`/admin/organizations/${state.orgId}/providers`),
  ]);
  const candidates = candidatesRes.candidates || [];
  const providers = (providersRes.providers || []).filter((p) => p.status === 'active');

  const supervisorOptions = candidates.map((u) => `<option value="${escapeHtml(u.id)}" ${isEdit && u.id === assignment.supervisor_user_id ? 'selected' : ''}>${escapeHtml(u.display_name || u.username)}</option>`).join('');
  const providerOptions = providers.map((p) => `<option value="${escapeHtml(p.id)}" ${isEdit && p.id === assignment.supervisee_provider_id ? 'selected' : ''}>${escapeHtml(p.display_name || p.username)}</option>`).join('');

  const startValue = assignment?.effective_start ? String(assignment.effective_start).split('T')[0] : '';
  const endValue = assignment?.effective_end ? String(assignment.effective_end).split('T')[0] : '';
  const activeChecked = isEdit ? assignment.active : true;
  const activeAttr = activeChecked ? 'checked' : '';
  const externalChecked = isEdit ? assignment.external : true;
  const externalAttr = externalChecked ? 'checked' : '';

  const title = isEdit ? 'Edit supervisor assignment' : 'Add supervisor assignment';
  const modalHtml = `
    <div class="modal-header">
      <h3 class="modal-title">${title}</h3>
      <button class="btn-close" id="close-modal">×</button>
    </div>
    <div class="modal-body" id="supervisor-form">
      <div class="form-group">
        <label for="supervisor-supervisor">Clinical Supervisor</label>
        <select id="supervisor-supervisor" class="form-control" ${isEdit ? 'disabled' : ''}>
          ${supervisorOptions}
        </select>
      </div>
      <div class="form-group">
        <label for="supervisor-supervisee">Supervisee / Provider</label>
        <select id="supervisor-supervisee" class="form-control" ${isEdit ? 'disabled' : ''}>
          ${providerOptions}
        </select>
      </div>
      <div class="form-group">
        <label for="supervisor-effective-start">Effective date</label>
        <input type="date" id="supervisor-effective-start" class="form-control" value="${startValue}">
      </div>
      <div class="form-group">
        <label for="supervisor-effective-end">Optional end date</label>
        <input type="date" id="supervisor-effective-end" class="form-control" value="${endValue}">
      </div>
      <div class="form-group form-inline">
        <label><input type="checkbox" id="supervisor-active" ${activeAttr}> Active</label>
      </div>
      <div class="form-group form-inline">
        <label><input type="checkbox" id="supervisor-external" ${externalAttr} ${isEdit ? 'disabled' : ''}> External non-provider supervisor</label>
      </div>
      <div class="notice">
        External non-provider supervisors require unanimous client approval before observing any client-containing session.
      </div>
      <div id="supervisor-error" class="error-text"></div>
    </div>
    <div class="modal-footer">
      <button class="btn" id="cancel-supervisor">Cancel</button>
      <button class="btn btn-primary" id="save-supervisor">${isEdit ? 'Save changes' : 'Create assignment'}</button>
    </div>
  `;

  const modal = openModal('supervisor-modal', modalHtml);
  const close = () => closeModal('supervisor-modal');
  $('#close-modal')?.addEventListener('click', close);
  $('#cancel-supervisor')?.addEventListener('click', close);
  $('#save-supervisor')?.addEventListener('click', async () => {
    const btn = $('#save-supervisor');
    btn.disabled = true;
    const body = {
      effectiveStart: $('#supervisor-effective-start').value || null,
      effectiveEnd: $('#supervisor-effective-end').value || null,
      active: $('#supervisor-active').checked,
    };
    if (!isEdit) {
      body.supervisorUserId = $('#supervisor-supervisor').value;
      body.superviseeProviderId = $('#supervisor-supervisee').value;
      body.external = $('#supervisor-external').checked;
    }
    const errorEl = $('#supervisor-error');
    try {
      const path = isEdit
        ? `/admin/organizations/${state.orgId}/supervisors/${assignment.id}`
        : `/admin/organizations/${state.orgId}/supervisors`;
      const method = isEdit ? 'PATCH' : 'POST';
      await api(path, { method, body });
      close();
      await loadSupervisorsTable();
    } catch (err) {
      errorEl.textContent = err.message || 'Unable to save assignment.';
      btn.disabled = false;
    }
  });
}

async function renderRoles(container) {
  container.innerHTML = `
    <h1 class="page-title">Roles & permissions</h1>
    <p class="page-description">Custom roles are permission bundles created from delegatable permissions you hold.</p>
    <div id="roles-body"></div>
  `;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/roles`);
    const roles = res.roles || [];
    const body = $('#roles-body');
    if (!roles.length) {
      body.innerHTML = '<p class="empty-state">No roles found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>Name</th><th>Scope</th><th>Builtin</th><th>Status</th><th>Permissions</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    roles.forEach((r) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${escapeHtml(r.name)}</td>
        <td>${escapeHtml(r.scope)}</td>
        <td>${r.is_builtin ? 'Yes' : 'No'}</td>
        <td><span class="badge ${r.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(r.status)}</span></td>
        <td>${(r.permissions || []).slice(0, 5).map(escapeHtml).join(', ')}${(r.permissions || []).length > 5 ? '...' : ''}</td>
      `;
      tbody.appendChild(tr);
    });
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load roles.');
  }
}

async function renderAcknowledgments(container) {
  container.innerHTML = `
    <h1 class="page-title">Acknowledgments</h1>
    <p class="page-description">Required session acknowledgments for this organization.</p>
    <div id="ack-body"></div>
  `;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/acknowledgments`);
    const acks = res.acknowledgments || [];
    const body = $('#ack-body');
    if (!acks.length) {
      body.innerHTML = '<p class="empty-state">No acknowledgment definitions found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>Type</th><th>Title</th><th>Version</th><th>Required</th><th>Active</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    acks.forEach((a) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${escapeHtml(a.acknowledgment_type)}</td>
        <td>${escapeHtml(a.title || '—')}</td>
        <td>${escapeHtml(a.version)}</td>
        <td>${a.is_required ? 'Yes' : 'No'}</td>
        <td><span class="badge ${a.active ? 'badge-green' : 'badge-gray'}">${a.active ? 'Yes' : 'No'}</span></td>
      `;
      tbody.appendChild(tr);
    });
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load acknowledgments.');
  }
}

async function renderAudit(container) {
  container.innerHTML = `
    <h1 class="page-title">Audit log</h1>
    <p class="page-description">Durable record of security-relevant events for this organization.</p>
    <div id="audit-body"></div>
  `;
  try {
    const res = await api('/audit');
    const events = res.events || [];
    const body = $('#audit-body');
    if (!events.length) {
      body.innerHTML = '<p class="empty-state">No audit events found.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>Time</th><th>Action</th><th>Actor</th><th>Target</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    events.slice(0, 100).forEach((e) => {
      const tr = document.createElement('tr');
      const meta = e.metadata ? JSON.stringify(e.metadata).slice(0, 80) : '';
      tr.innerHTML = `
        <td>${new Date(e.created_at || e.event_time).toLocaleString()}</td>
        <td>${escapeHtml(e.action)}</td>
        <td>${escapeHtml(e.actor_type)}:${escapeHtml(e.actor_id)}</td>
        <td>${escapeHtml(e.target_type || '')}:${escapeHtml(e.target_id || '')} ${escapeHtml(meta)}</td>
      `;
      tbody.appendChild(tr);
    });
    body.appendChild(table);
  } catch (err) {
    setError('Unable to load audit log.');
  }
}

function renderNotifications(container) {
  container.innerHTML = `
    <div class="page-header">
      <div>
        <h1 class="page-title">Notifications</h1>
        <p class="page-description">TeleMed operational notices requiring awareness or follow-up.</p>
      </div>
      <button class="btn" id="notifications-mark-all-read">Mark all as read</button>
    </div>
    <div class="summary-grid" id="notification-summary">
      <div class="summary-card"><div class="summary-label">Unread</div><div class="summary-number" id="notif-unread">-</div></div>
      <div class="summary-card"><div class="summary-label">Total</div><div class="summary-number" id="notif-total">-</div></div>
      <div class="summary-card"><div class="summary-label">Needs attention</div><div class="summary-number" id="notif-attention">-</div></div>
      <div class="summary-card"><div class="summary-label">This page</div><div class="summary-number" id="notif-page-count">-</div></div>
    </div>
    <div class="notification-page-toolbar">
      <select id="notif-filter-status" class="notification-filter">
        <option value="all">All statuses</option>
        <option value="unread">Unread</option>
        <option value="read">Read</option>
      </select>
      <select id="notif-filter-type" class="notification-filter">
        <option value="all">All notification types</option>
        <option value="participant_reassigned">Participant changes</option>
        <option value="session_coverage">Session coverage</option>
        <option value="supervisor_requested">Clinical supervision</option>
        <option value="supervisor_authorized">Clinical supervision</option>
        <option value="supervisor_denied">Clinical supervision</option>
        <option value="supervisor_must_leave">Clinical supervision</option>
        <option value="provider_status_changed">Administrative</option>
        <option value="user_membership_status_changed">Administrative</option>
        <option value="supervisor_assignment_changed">Clinical supervision</option>
        <option value="document_distributed">Documents</option>
      </select>
    </div>
    <div class="panel" id="notif-list"></div>
  `;
  $('#notif-filter-status').value = state.notifications.filterStatus;
  $('#notif-filter-type').value = state.notifications.filterType;
  $('#notif-filter-status').addEventListener('change', (e) => {
    state.notifications.filterStatus = e.target.value;
    state.notifications.page = 0;
    loadNotificationPage();
  });
  $('#notif-filter-type').addEventListener('change', (e) => {
    state.notifications.filterType = e.target.value;
    state.notifications.page = 0;
    loadNotificationPage();
  });
  $('#notifications-mark-all-read').addEventListener('click', markAllRead);
  loadNotificationPage();
}

async function loadNotificationPage() {
  const list = $('#notif-list');
  if (!list) return;
  try {
    const params = new URLSearchParams();
    params.set('limit', String(state.notifications.limit));
    params.set('page', String(state.notifications.page));
    if (state.notifications.filterStatus !== 'all') params.set('status', state.notifications.filterStatus);
    if (state.notifications.filterType !== 'all') params.set('type', state.notifications.filterType);
    const data = await api(`/notifications?${params.toString()}`);
    state.notifications.items = data.notifications || [];
    state.notifications.total = data.total || 0;
    state.notifications.unread = data.unread || 0;
    renderNotificationBell();

    $('#notif-unread').textContent = data.unread || 0;
    $('#notif-total').textContent = data.total || 0;
    $('#notif-attention').textContent = data.notifications.filter((n) => n.type === 'supervisor_must_leave' || n.type === 'supervisor_denied' || n.type === 'supervisor_requested').length;
    $('#notif-page-count').textContent = data.notifications.length;

    if (!data.notifications.length) {
      list.innerHTML = '<div class="empty-state">No notifications match the selected filters.</div>';
      return;
    }

    list.innerHTML = '';
    data.notifications.forEach((n) => {
      const row = document.createElement('div');
      row.className = 'notification-row';
      if (n.status === 'unread') row.classList.add('unread');
      row.dataset.id = n.id;
      row.innerHTML = `
        <div class="notification-icon ${typeIconClass(n.type)}">${typeInitial(n.type)}</div>
        <div>
          <div class="notification-title">${escapeHtml(n.title)}</div>
          <div class="notification-text">${escapeHtml(n.message)}</div>
          <div class="notification-meta">
            <span class="badge ${typeIconClass(n.type).replace('notification-icon', 'badge')}">${typeLabel(n.type)}</span>
            <span class="notification-time">${relativeTime(n.created_at)}</span>
          </div>
        </div>
        <div class="notification-actions">
          <button class="btn btn-sm toggle-read" data-id="${escapeHtml(n.id)}" data-status="${n.status}">
            ${n.status === 'unread' ? 'Mark read' : 'Mark unread'}
          </button>
          ${n.destination ? `<button class="btn btn-sm view-destination" data-destination="${escapeHtml(n.destination)}">View</button>` : ''}
        </div>
      `;
      row.addEventListener('click', (e) => {
        if (e.target.classList.contains('toggle-read')) {
          markNotificationRead(n.id, n.status, e);
          return;
        }
        if (e.target.classList.contains('view-destination')) {
          const dest = e.target.dataset.destination;
          if (dest) navigateTo(dest);
          return;
        }
        toggleNotificationDetail(n.id);
      });
      list.appendChild(row);

      const detail = document.createElement('div');
      detail.className = 'notification-detail hidden';
      detail.id = `notif-detail-${n.id}`;
      detail.innerHTML = `
        <div class="notification-text">${escapeHtml(n.message)}</div>
        ${n.entity_type ? `<div class="notification-meta" style="margin-top:8px"><span class="muted">Reference:</span> ${escapeHtml(n.entity_type)} ${escapeHtml(n.entity_id || '')}</div>` : ''}
      `;
      list.appendChild(detail);
    });

    list.appendChild(renderNotificationPagination());

    list.querySelectorAll('.toggle-read').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = btn.dataset.id;
        const status = btn.dataset.status;
        markNotificationRead(id, status, e);
      });
    });
  } catch (_err) {
    list.innerHTML = '<div class="error-text">Unable to load notifications.</div>';
  }
}

function toggleNotificationDetail(id) {
  const detail = $(`#notif-detail-${id}`);
  if (detail) detail.classList.toggle('hidden');
}

function renderNotificationPagination() {
  const total = state.notifications.total || 0;
  const limit = state.notifications.limit;
  const page = state.notifications.page;
  const lastPage = Math.max(0, Math.ceil(total / limit) - 1);
  const wrap = document.createElement('div');
  wrap.className = 'notification-pagination';
  wrap.innerHTML = `
    <button class="btn" id="notif-prev" ${page <= 0 ? 'disabled' : ''}>Previous</button>
    <span class="muted">Page ${page + 1} of ${lastPage + 1}</span>
    <button class="btn" id="notif-next" ${page >= lastPage ? 'disabled' : ''}>Next</button>
  `;
  wrap.querySelector('#notif-prev')?.addEventListener('click', () => {
    if (page > 0) {
      state.notifications.page -= 1;
      loadNotificationPage();
    }
  });
  wrap.querySelector('#notif-next')?.addEventListener('click', () => {
    if (page < lastPage) {
      state.notifications.page += 1;
      loadNotificationPage();
    }
  });
  return wrap;
}

async function renderSettings(container) {
  container.innerHTML = `
    <h1 class="page-title">Organization settings</h1>
    <p class="page-description">Telehealth Platform-specific settings for this organization.</p>
    <div id="settings-body"></div>
  `;
  try {
    const res = await api(`/admin/organizations/${state.orgId}/settings`);
    const settings = res.settings || {};
    $('#settings-body').innerHTML = `
      <div class="form-section">
        <div class="form-section-title">Organization identity</div>
        <div class="field-grid">
          <div class="field"><label>Organization name</label><input type="text" id="setting-name" value="${escapeHtml(settings.name || state.orgName)}"/></div>
          <div class="field"><label>Default room capacity</label><input type="number" id="setting-capacity" value="${settings.defaultRoomCapacity || 12}"/></div>
          <div class="field"><label>Early entry window (minutes)</label><input type="number" id="setting-early" value="${settings.clientEarlyMinutes || 60}"/></div>
          <div class="field"><label>Late entry window (minutes)</label><input type="number" id="setting-late" value="${settings.clientLateMinutes || 60}"/></div>
          <div class="field"><label>Default timezone</label><input type="text" id="setting-timezone" value="${escapeHtml(settings.defaultTimezone || 'UTC')}"/></div>
        </div>
      </div>
      <button class="btn btn-primary" id="save-settings">Save settings</button>
      <span class="error-text" id="settings-error"></span>
    `;
    $('#save-settings').addEventListener('click', async () => {
      try {
        await api(`/admin/organizations/${state.orgId}/settings`, {
          method: 'PATCH',
          body: {
            name: $('#setting-name').value,
            defaultRoomCapacity: parseInt($('#setting-capacity').value, 10),
            clientEarlyMinutes: parseInt($('#setting-early').value, 10),
            clientLateMinutes: parseInt($('#setting-late').value, 10),
            defaultTimezone: $('#setting-timezone').value,
          },
        });
        $('#settings-error').textContent = 'Saved.';
        await refreshAuth();
      } catch (err) {
        $('#settings-error').textContent = err.message || 'Save failed.';
      }
    });
  } catch (err) {
    $('#settings-body').innerHTML = '<p class="error-text">Unable to load settings.</p>';
  }
}

function stopDevicePreview() {
  if (devicePreviewCleanup) { devicePreviewCleanup(); devicePreviewCleanup = null; }
  if (micActivityCleanup) { micActivityCleanup(); micActivityCleanup = null; }
}

function selectOption(selectEl, value) {
  if (!selectEl || !value) return;
  for (const opt of selectEl.options) {
    if (opt.value === value) {
      selectEl.value = value;
      return;
    }
  }
}

async function renderDevices(container) {
  stopDevicePreview();
  container.innerHTML = `
    <h1 class="page-title">Device settings</h1>
    <p class="page-description">Choose the camera and microphone to use for sessions on this browser.</p>
    <div class="panel" id="devices-panel">
      <div id="device-warning" class="error-text" role="alert"></div>
      <button type="button" id="device-permission-btn" class="btn btn-primary" hidden>Allow camera &amp; microphone</button>
      <div class="device-preview-wrap">
        <video id="device-preview-video" autoplay playsinline muted style="max-width:100%;width:320px;border-radius:6px;background:#000;"></video>
        <div class="field">
          <label for="device-mic-activity">Microphone activity</label>
          <meter id="device-mic-activity" min="0" max="1" low="0.1" high="0.3" optimum="0.5" value="0"></meter>
        </div>
      </div>
      <div class="field"><label for="device-cam-select">Camera</label><select id="device-cam-select" class="device-select"></select></div>
      <div class="field"><label for="device-mic-select">Microphone</label><select id="device-mic-select" class="device-select"></select></div>
      <div class="field" id="device-speaker-wrap"><label for="device-speaker-select">Speaker</label><select id="device-speaker-select" class="device-select"></select></div>
      <p class="small muted" id="device-status-msg"></p>
    </div>
  `;

  const warning = $('#device-warning');
  const permissionBtn = $('#device-permission-btn');
  const statusMsg = $('#device-status-msg');
  const userId = state.user?.userId;

  async function refresh(preferDefaults = false) {
    const preference = loadDevicePreference(userId);
    const useSaved = !preferDefaults;
    try {
      const stream = await requestPreflightStream({
        videoDeviceId: (useSaved ? preference.cameraId : undefined) || undefined,
        audioDeviceId: (useSaved ? preference.micId : undefined) || undefined,
      });
      devicePreviewCleanup = () => stopStream(stream);
      $('#device-preview-video').srcObject = stream;

      const micMeter = $('#device-mic-activity');
      micActivityCleanup = createMicActivityIndicator(stream, {
        onLevel: (level) => { if (micMeter) micMeter.value = level; },
      });

      const devices = await listDevices();
      populateDeviceSelect($('#device-cam-select'), devices.cameras, 'Camera');
      populateDeviceSelect($('#device-mic-select'), devices.microphones, 'Microphone');
      selectOption($('#device-cam-select'), useSaved ? preference.cameraId : undefined);
      selectOption($('#device-mic-select'), useSaved ? preference.micId : undefined);

      const speakerWrap = $('#device-speaker-wrap');
      if (SessionView.speakerSelectionSupported() && devices.speakers.length > 0) {
        speakerWrap.hidden = false;
        populateDeviceSelect($('#device-speaker-select'), devices.speakers, 'Speaker');
        selectOption($('#device-speaker-select'), preference.speakerId);
      } else {
        speakerWrap.hidden = true;
      }

      if (permissionBtn) permissionBtn.hidden = true;
      if (warning) warning.textContent = '';
      if (statusMsg) {
        const savedMissing = !useSaved && (preference.cameraId || preference.micId);
        const camLabel = $('#device-cam-select').selectedOptions[0]?.textContent || 'Default';
        const micLabel = $('#device-mic-select').selectedOptions[0]?.textContent || 'Default';
        statusMsg.textContent = savedMissing
          ? `Saved device no longer available. Using: ${camLabel} / ${micLabel}`
          : `Selected: ${camLabel} / ${micLabel}`;
      }
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (warning) warning.textContent = 'Camera/microphone permission is required to choose devices.';
        if (permissionBtn) {
          permissionBtn.hidden = false;
          permissionBtn.onclick = () => { permissionBtn.hidden = true; refresh(); };
        }
      } else if (!preferDefaults && (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.name === 'OverconstrainedError')) {
        // Saved device may have been disconnected; fall back to defaults once.
        if (statusMsg) statusMsg.textContent = 'Saved device not found; trying default devices.';
        await refresh(true);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.name === 'OverconstrainedError') {
        if (warning) warning.textContent = 'No camera or microphone found. Please connect a device.';
      } else {
        if (warning) warning.textContent = 'Unable to access camera/microphone. Please check permissions.';
      }
    }
  }

  $('#device-cam-select').addEventListener('change', async (e) => {
    const cameraId = e.target.value;
    const micId = $('#device-mic-select').value;
    stopStream($('#device-preview-video').srcObject);
    try {
      const stream = await requestPreflightStream({ videoDeviceId: cameraId, audioDeviceId: micId });
      $('#device-preview-video').srcObject = stream;
      updateMicActivity(stream);
      savePreferredDevice(userId, 'camera', cameraId);
    } catch (err) {
      if (statusMsg) statusMsg.textContent = 'Could not switch camera. The previous camera may have been disconnected.';
    }
  });

  $('#device-mic-select').addEventListener('change', async (e) => {
    const micId = e.target.value;
    const cameraId = $('#device-cam-select').value;
    stopStream($('#device-preview-video').srcObject);
    try {
      const stream = await requestPreflightStream({ videoDeviceId: cameraId, audioDeviceId: micId });
      $('#device-preview-video').srcObject = stream;
      updateMicActivity(stream);
      savePreferredDevice(userId, 'microphone', micId);
    } catch (err) {
      if (statusMsg) statusMsg.textContent = 'Could not switch microphone. The previous microphone may have been disconnected.';
    }
  });

  const speakerSel = $('#device-speaker-select');
  if (speakerSel) {
    speakerSel.addEventListener('change', (e) => {
      const video = $('#device-preview-video');
      if (video && typeof video.setSinkId === 'function') {
        try { video.setSinkId(e.target.value); } catch (_e) {}
      }
      savePreferredDevice(userId, 'speaker', e.target.value);
    });
  }

  function updateMicActivity(stream) {
    if (micActivityCleanup) { micActivityCleanup(); micActivityCleanup = null; }
    const micMeter = $('#device-mic-activity');
    micActivityCleanup = createMicActivityIndicator(stream, {
      onLevel: (level) => { if (micMeter) micMeter.value = level; },
    });
  }

  await refresh();
}

async function renderSupervision(container) {
  container.innerHTML = `
    <h1 class="page-title">Supervision</h1>
    <p class="page-description">Observe assigned supervisee sessions.</p>
    <div class="notice">Clinical Supervisor access does not provide provider, scheduling, participant-management, or administrative authority. Observation is limited to sessions involving a currently assigned supervisee.</div>
    <div id="supervision-body"></div>
  `;
  try {
    const sessionsRes = await api('/rooms/supervisor/sessions');
    const sessions = sessionsRes.sessions || [];
    const body = $('#supervision-body');
    if (!sessions.length) {
      body.innerHTML = '<p class="empty-state">No observable sessions for today.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `
      <thead><tr><th>Time</th><th>Session</th><th>Supervisee</th><th>Status</th><th></th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    sessions.forEach((s) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${formatTime(s.startTime)} - ${formatTime(s.endTime)}</td>
        <td><strong>${escapeHtml(s.roomLabel || 'Session')}</strong></td>
        <td>${escapeHtml(s.actualProviderDisplayName || 'Provider')}</td>
        <td><span class="badge ${statusBadge(s.status)}">${escapeHtml(s.status)}</span></td>
        <td><button class="btn btn-primary observe-btn" data-code="${escapeHtml(s.roomCode)}" data-session="${escapeHtml(s.id)}">Observe</button></td>
      `;
      tbody.appendChild(tr);
    });
    body.appendChild(table);
    $$('.observe-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const slug = document.documentElement.dataset.tenantSlug || '';
        const prefix = slug ? `/${slug}` : '';
        window.location.href = `${prefix}/supervisor/session/${encodeURIComponent(btn.dataset.code)}?sessionId=${encodeURIComponent(btn.dataset.session)}`;
      });
    });
  } catch (err) {
    setError('Unable to load supervision sessions.');
  }
}

function renderPlatformAdmin(container) {
  container.innerHTML = `
    <h1 class="page-title">Platform administration</h1>
    <p class="page-description">Cross-organization platform management.</p>
    <div id="platform-body"></div>
  `;
  try {
    api('/admin/organizations').then((res) => {
      const orgs = res.organizations || [];
      const body = $('#platform-body');
      const table = document.createElement('table');
      table.innerHTML = `<thead><tr><th>Organization</th><th>Enterprise</th><th>Status</th></tr></thead><tbody></tbody>`;
      const tbody = table.querySelector('tbody');
      orgs.forEach((o) => {
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td>${escapeHtml(o.name)}</td>
          <td>${escapeHtml(o.enterprise_name || '—')}</td>
          <td><span class="badge ${o.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(o.status)}</span></td>
        `;
        tbody.appendChild(tr);
      });
      body.innerHTML = '';
      body.appendChild(table);
    });
  } catch (err) {
    setError('Unable to load platform organizations.');
  }
}

function toggleMyGroup(id) {
  const detail = $(`#my-group-detail-${id}`);
  if (!detail) return;
  detail.classList.toggle('open');
  if (detail.classList.contains('open')) {
    loadMyGroupDetail(id);
  }
}

async function loadMyGroupDetail(id) {
  const body = $(`#my-group-detail-${id}`);
  if (!body) return;
  body.innerHTML = '<p class="empty-state">Loading group detail...</p>';
  try {
    const data = await api(`/provider/groups/${id}`);
    const group = data.group || {};
    const participants = data.participants || [];
    const sessions = data.upcomingSessions || [];

    const participantsHtml = participants.length
      ? `<table class="group-participants-table"><thead>
           <tr><th>Participant</th><th>Portal</th><th>Joined group</th><th>Status</th><th></th></tr>
         </thead><tbody>
           ${participants.map((p) => `
             <tr>
               <td><strong>${escapeHtml(p.displayName || p.username || 'Unknown')}</strong></td>
               <td><span class="badge badge-green">Active</span></td>
               <td>${p.joinDate ? formatDate(p.joinDate) : '—'}</td>
               <td>${escapeHtml(p.status || 'active')}</td>
               <td class="group-participant-actions">
                 <button class="btn" data-reassign data-client-id="${p.id}" data-client-name="${escapeHtml(p.displayName || p.username || 'Unknown')}">Reassign</button>
                 <button class="btn btn-danger" data-remove data-client-id="${p.id}" data-client-name="${escapeHtml(p.displayName || p.username || 'Unknown')}">Remove</button>
               </td>
             </tr>
           `).join('')}
         </tbody></table>`
      : '<p class="empty-state">No active participants in this group.</p>';

    const sessionsHtml = sessions.length
      ? `<table class="group-sessions-table"><thead>
           <tr><th>Date</th><th>Time</th><th>Scheduled provider</th><th>Actual provider</th><th>Status</th></tr>
         </thead><tbody>
           ${sessions.map((s) => `
             <tr>
               <td>${formatDate(s.sessionDate)}</td>
               <td>${formatTime(s.startTime)} - ${formatTime(s.endTime)}</td>
               <td>${escapeHtml(s.scheduledProviderDisplayName)}</td>
               <td>${escapeHtml(s.actualProviderDisplayName || s.scheduledProviderDisplayName)}${s.isCoverage ? ' <span class="badge badge-amber">Coverage</span>' : ''}</td>
               <td><span class="badge badge-gray">${escapeHtml(s.status)}</span></td>
             </tr>
           `).join('')}
         </tbody></table>`
      : '<p class="empty-state">No upcoming sessions scheduled for this group.</p>';

    body.innerHTML = `
      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">Active participants</div>
          <div class="summary-value">${group.activeParticipantCount}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Available capacity</div>
          <div class="summary-value">${Math.max(0, group.maxClients - group.activeParticipantCount)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Max capacity</div>
          <div class="summary-value">${group.maxClients}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Primary provider</div>
          <div class="summary-value small-value">${escapeHtml(group.providerDisplayName)}</div>
        </div>
      </div>
      <div class="group-detail-section">
        <div class="group-section-header">
          <h3 class="section-title">Participants</h3>
          <button class="btn btn-primary" id="add-participant-${id}">+ Add participant</button>
        </div>
        ${participantsHtml}
      </div>
      <div class="group-detail-section">
        <h3 class="section-title">Upcoming sessions</h3>
        ${sessionsHtml}
      </div>
    `;

    const addBtn = $(`#add-participant-${id}`);
    if (addBtn) addBtn.addEventListener('click', () => openAddParticipantModal(id, group.label));

    body.querySelectorAll('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', () => openRemoveParticipantModal(id, group.label, btn.dataset.clientId, btn.dataset.clientName));
    });
    body.querySelectorAll('[data-reassign]').forEach((btn) => {
      btn.addEventListener('click', () => openReassignParticipantModal(id, group.label, btn.dataset.clientId, btn.dataset.clientName));
    });
  } catch (err) {
    body.innerHTML = `<p class="empty-state">Unable to load group detail: ${escapeHtml(err.message || 'unknown error')}</p>`;
  }
}

function openModal(id, html) {
  let modal = $('#' + id);
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = id;
  modal.className = 'modal-backdrop open';
  modal.innerHTML = `<div class="modal">${html}</div>`;
  document.body.appendChild(modal);
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
  return modal;
}

function closeModal(id) {
  const modal = $('#' + id);
  if (modal) modal.remove();
}

async function openAddParticipantModal(groupId, groupLabel) {
  try {
    const { clients } = await api(`/provider/groups/${groupId}/eligible-clients`);
    if (!clients.length) {
      openModal('add-participant-modal', `
        <div class="modal-header"><strong>Add participant</strong><button class="btn" id="close-add">Close</button></div>
        <div class="modal-body"><p class="empty-state">No eligible clients are available for this group.</p></div>
        <div class="modal-footer"><button class="btn" id="cancel-add">Close</button></div>
      `);
      $('#close-add')?.addEventListener('click', () => closeModal('add-participant-modal'));
      $('#cancel-add')?.addEventListener('click', () => closeModal('add-participant-modal'));
      return;
    }
    const options = clients.map((c) => `<option value="${c.id}">${escapeHtml(c.displayName || c.username || 'Unknown')}</option>`).join('');
    openModal('add-participant-modal', `
      <div class="modal-header"><strong>Add participant</strong><button class="btn" id="close-add">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Participant</label><select id="add-participant-client">${options}</select></div>
        <div class="field"><label>Group</label><input value="${escapeHtml(groupLabel)}" disabled></div>
        <div class="note">Documents previously distributed to this group will not automatically become available to the new participant.</div>
        <div class="error-text" id="add-participant-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-add">Cancel</button>
        <button class="btn btn-primary" id="submit-add">Add participant</button>
      </div>
    `);
    $('#close-add')?.addEventListener('click', () => closeModal('add-participant-modal'));
    $('#cancel-add')?.addEventListener('click', () => closeModal('add-participant-modal'));
    $('#submit-add')?.addEventListener('click', async () => {
      const clientId = $('#add-participant-client').value;
      try {
        await api(`/provider/groups/${groupId}/participants`, { method: 'POST', body: { clientId } });
        closeModal('add-participant-modal');
        await loadMyGroupDetail(groupId);
      } catch (err) {
        $('#add-participant-error').textContent = err.message || 'Add failed.';
      }
    });
  } catch (err) {
    console.error('openAddParticipantModal failed:', err);
    setError('Unable to load eligible clients: ' + (err.message || 'unknown error'));
  }
}

function openRemoveParticipantModal(groupId, groupLabel, clientId, clientName) {
  openModal('remove-participant-modal', `
    <div class="modal-header"><strong>Remove from group</strong><button class="btn" id="close-remove">Close</button></div>
    <div class="modal-body">
      <p>Remove <strong>${escapeHtml(clientName)}</strong> from <strong>${escapeHtml(groupLabel)}</strong>?</p>
      <div class="note">This is a Telehealth group removal, not a clinical discharge.</div>
      <div class="error-text" id="remove-participant-error"></div>
    </div>
    <div class="modal-footer">
      <button class="btn" id="cancel-remove">Cancel</button>
      <button class="btn btn-danger" id="submit-remove">Remove from group</button>
    </div>
  `);
  $('#close-remove')?.addEventListener('click', () => closeModal('remove-participant-modal'));
  $('#cancel-remove')?.addEventListener('click', () => closeModal('remove-participant-modal'));
  $('#submit-remove')?.addEventListener('click', async () => {
    try {
      await api(`/provider/groups/${groupId}/participants/${clientId}`, { method: 'PATCH' });
      closeModal('remove-participant-modal');
      await loadMyGroupDetail(groupId);
    } catch (err) {
      $('#remove-participant-error').textContent = err.message || 'Remove failed.';
    }
  });
}

async function openReassignParticipantModal(groupId, groupLabel, clientId, clientName) {
  try {
    const { targets } = await api('/provider/groups/reassignment-targets');
    const choices = targets.filter((t) => t.roomId !== groupId);
    if (!choices.length) {
      openModal('reassign-participant-modal', `
        <div class="modal-header"><strong>Reassign participant</strong><button class="btn" id="close-reassign">Close</button></div>
        <div class="modal-body"><p class="empty-state">No valid target groups are available.</p></div>
        <div class="modal-footer"><button class="btn" id="cancel-reassign">Close</button></div>
      `);
      $('#close-reassign')?.addEventListener('click', () => closeModal('reassign-participant-modal'));
      $('#cancel-reassign')?.addEventListener('click', () => closeModal('reassign-participant-modal'));
      return;
    }
    const options = choices.map((t) => `<option value="${t.roomId}">${escapeHtml(t.label)} — ${escapeHtml(t.providerDisplayName)}</option>`).join('');
    openModal('reassign-participant-modal', `
      <div class="modal-header"><strong>Reassign participant</strong><button class="btn" id="close-reassign">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Participant</label><input value="${escapeHtml(clientName)}" disabled></div>
        <div class="field"><label>Current group</label><input value="${escapeHtml(groupLabel)}" disabled></div>
        <div class="field"><label>Target group</label><select id="reassign-target">${options}</select></div>
        <div class="note">The participant's active membership will move to the target group. The original provider will be notified.</div>
        <div class="error-text" id="reassign-participant-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-reassign">Cancel</button>
        <button class="btn btn-primary" id="submit-reassign">Reassign</button>
      </div>
    `);
    $('#close-reassign')?.addEventListener('click', () => closeModal('reassign-participant-modal'));
    $('#cancel-reassign')?.addEventListener('click', () => closeModal('reassign-participant-modal'));
    $('#submit-reassign')?.addEventListener('click', async () => {
      const targetId = $('#reassign-target').value;
      try {
        await api(`/participants/${clientId}/provider-room`, { method: 'PATCH', body: { providerRoomId: targetId } });
        closeModal('reassign-participant-modal');
        await loadMyGroupDetail(groupId);
      } catch (err) {
        $('#reassign-participant-error').textContent = err.message || 'Reassignment failed.';
      }
    });
  } catch (err) {
    console.error('openReassignParticipantModal failed:', err);
    setError('Unable to load reassignment targets: ' + (err.message || 'unknown error'));
  }
}

async function renderMyGroups(container) {
  container.innerHTML = `
    <h1 class="page-title">My groups</h1>
    <p class="page-description">Provider room groups and participants.</p>
    <div id="my-groups-body"><p class="empty-state">Loading...</p></div>
  `;
  try {
    const data = await api('/provider/groups');
    const body = $('#my-groups-body');
    const groups = data.groups || [];
    if (!groups.length) {
      body.innerHTML = '<p class="empty-state">You do not have any assigned groups.</p>';
      return;
    }
    body.innerHTML = groups.map((g) => `
      <div class="group-card" id="my-group-card-${g.id}">
        <div class="group-header" id="my-group-header-${g.id}" role="button" tabindex="0">
          <div>
            <div class="group-name">${escapeHtml(g.label)}</div>
            <div class="group-meta">Primary provider: ${escapeHtml(g.providerDisplayName)}</div>
          </div>
          <div>
            <div class="label">Participants</div>
            <div class="value">${g.activeParticipantCount} / ${g.maxClients}</div>
          </div>
          <div>
            <div class="label">Next session</div>
            <div class="value">${g.nextSessionDate ? formatDate(g.nextSessionDate) : '—'}</div>
          </div>
          <div>
            <span class="badge ${g.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(g.status)}</span>
          </div>
        </div>
        <div class="group-detail" id="my-group-detail-${g.id}">
          <p class="empty-state">Select the group header to load details.</p>
        </div>
      </div>
    `).join('');

    groups.forEach((g) => {
      const header = $(`#my-group-header-${g.id}`);
      if (header) {
        header.addEventListener('click', () => toggleMyGroup(g.id));
        header.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') toggleMyGroup(g.id); });
      }
    });
  } catch (err) {
    setError('Unable to load groups: ' + (err.message || 'unknown error'));
    $('#my-groups-body').innerHTML = '<p class="empty-state">Unable to load groups.</p>';
  }
}

/* -------------------------------------------------------------------------- */
/* Shared helpers                                                             */
/* -------------------------------------------------------------------------- */
function renderSessionTable(container, sessions, { showActions = false } = {}) {
  if (!sessions.length) {
    container.innerHTML = '<p class="empty-state">No sessions found.</p>';
    return;
  }
  const table = document.createElement('table');
  table.innerHTML = `
    <thead><tr><th>Time</th><th>Session</th><th>Provider</th><th>Status</th>${showActions ? '<th></th>' : ''}</tr></thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody');
  sessions.forEach((s) => {
    const tr = document.createElement('tr');
    tr.innerHTML = `
      <td>${formatTime(s.startTime)} - ${formatTime(s.endTime)}</td>
      <td>${escapeHtml(s.roomLabel || 'Session')}</td>
      <td>${escapeHtml(s.actualProviderDisplayName || '—')}${s.isCoverage ? ' <span class="badge badge-amber">coverage</span>' : ''}</td>
      <td><span class="badge ${statusBadge(s.status)}">${escapeHtml(s.status)}</span></td>
      ${showActions ? '<td class="actions-cell"></td>' : ''}
    `;
    tbody.appendChild(tr);
  });
  container.innerHTML = '';
  container.appendChild(table);
}

function togglePanel(id) {
  const el = typeof id === 'string' ? $(`#${id}`) : id;
  if (el) el.classList.toggle('collapsed');
}

function stopSessionPoll() {
  if (state.sessionPoll) {
    clearInterval(state.sessionPoll);
    state.sessionPoll = null;
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* -------------------------------------------------------------------------- */
/* Documents                                                                  */
/* -------------------------------------------------------------------------- */
function renderDocuments(container) {
  const isOrgAdmin = hasPermission('document.manage');
  container.innerHTML = `
    <h1 class="page-title">Document library</h1>
    <p class="page-description">Manage reusable Telehealth Platform resources and distributions.</p>
    <div class="tabs">
      <button class="tab active" data-doc-tab="library">Library</button>
      <button class="tab" data-doc-tab="folders">Folders</button>
      <button class="tab" data-doc-tab="distributions">Distributions</button>
    </div>
    <section id="doc-tab-library" class="tab-page active"></section>
    <section id="doc-tab-folders" class="tab-page"></section>
    <section id="doc-tab-distributions" class="tab-page"></section>
  `;

  const showDocTab = (name) => {
    $$('[data-doc-tab]').forEach((btn) => btn.classList.toggle('active', btn.dataset.docTab === name));
    $$('.tab-page').forEach((page) => page.classList.toggle('active', page.id === `doc-tab-${name}`));
  };

  $('[data-doc-tab="library"]').addEventListener('click', () => { showDocTab('library'); loadLibrary(); });
  $('[data-doc-tab="folders"]').addEventListener('click', () => { showDocTab('folders'); loadFolders(); });
  $('[data-doc-tab="distributions"]').addEventListener('click', () => { showDocTab('distributions'); loadDistributions(); });

  const base = container;

  async function loadLibrary() {
    const page = $('#doc-tab-library');
    page.innerHTML = '<p class="empty-state">Loading documents...</p>';
    try {
      const [docsRes, foldersRes, scopesRes] = await Promise.all([
        api('/documents'),
        api('/documents/folders'),
        api('/documents/distribution-scopes'),
      ]);
      const docs = docsRes.documents || [];
      const folders = foldersRes.folders || [];
      const scopes = scopesRes;
      page.innerHTML = '';
      const toolbar = document.createElement('div');
      toolbar.className = 'toolbar';
      toolbar.innerHTML = `
        <div class="toolbar-left">
          <input type="text" id="doc-search" class="search" placeholder="Search documents..." style="min-width:260px">
          <select id="doc-folder-filter"><option value="">All folders</option></select>
          <select id="doc-status-filter"><option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
        </div>
        <div class="toolbar-right">
          <button class="btn btn-primary" id="upload-doc-btn">+ Upload document</button>
        </div>
      `;
      page.appendChild(toolbar);

      const folderSelect = $('#doc-folder-filter');
      folders.forEach((f) => {
        const opt = document.createElement('option');
        opt.value = f.id;
        opt.textContent = f.name;
        folderSelect.appendChild(opt);
      });

      const panel = document.createElement('div');
      panel.className = 'panel';
      panel.innerHTML = '<div class="panel-header">Master document library</div><div class="panel-body" id="docs-table-wrap"></div>';
      page.appendChild(panel);

      renderDocsTable(docs, folders);

      $('#upload-doc-btn').addEventListener('click', () => openUploadModal(folders));

      const filter = () => {
        const q = $('#doc-search').value.toLowerCase();
        const f = $('#doc-folder-filter').value;
        const s = $('#doc-status-filter').value;
        renderDocsTable(docs.filter((d) => {
          return (!q || d.title.toLowerCase().includes(q) || (d.uploader_display_name || '').toLowerCase().includes(q))
            && (!f || d.folder_id === f)
            && (!s || d.status === s);
        }), folders);
      };
      $('#doc-search').addEventListener('input', filter);
      $('#doc-folder-filter').addEventListener('change', filter);
      $('#doc-status-filter').addEventListener('change', filter);
    } catch (err) {
      page.innerHTML = '<p class="empty-state">Unable to load documents.</p>';
    }
  }

  function renderDocsTable(docs, folders) {
    const wrap = $('#docs-table-wrap');
    if (!wrap) return;
    if (!docs.length) {
      wrap.innerHTML = '<p class="empty-state">No documents match the current filters.</p>';
      return;
    }
    const table = document.createElement('table');
    table.innerHTML = `<thead><tr><th>Document</th><th>Folder</th><th>Version</th><th>Uploaded by</th><th>Status</th><th></th></tr></thead><tbody></tbody>`;
    const tbody = table.querySelector('tbody');
    docs.forEach((d) => {
      const tr = document.createElement('tr');
      const folderName = folders.find((f) => f.id === d.folder_id)?.name || '—';
      tr.innerHTML = `
        <td><div class="name">${escapeHtml(d.title)}</div><div class="subtext">${escapeHtml(d.original_filename || '')}</div></td>
        <td>${escapeHtml(folderName)}</td>
        <td>${d.version_number || '—'}</td>
        <td>${escapeHtml(d.uploader_display_name || '—')}${d.uploader_user_id === state.userId ? '' : ' <span class="subtext">(Provider upload)</span>'}</td>
        <td><span class="badge ${d.status === 'active' ? 'badge-green' : 'badge-gray'}">${escapeHtml(d.status)}</span></td>
        <td style="text-align:right" class="actions-cell"></td>
      `;
      const actions = tr.querySelector('.actions-cell');
      if (d.status === 'active') {
        addBtn(actions, 'Distribute', 'btn btn-primary', () => openDistributeModal(d, folders, scopes));
      }
      if (d.canManage) {
        addBtn(actions, 'New version', 'btn', () => openVersionModal(d));
        addBtn(actions, d.status === 'active' ? 'Inactivate' : 'Activate', 'btn', () => toggleDocStatus(d));
      }
      tbody.appendChild(tr);
    });
    wrap.innerHTML = '';
    wrap.appendChild(table);
  }

  async function loadFolders() {
    const page = $('#doc-tab-folders');
    page.innerHTML = '<p class="empty-state">Loading folders...</p>';
    try {
      const res = await api('/documents/folders');
      const folders = res.folders || [];
      page.innerHTML = '';
      const panel = document.createElement('div');
      panel.className = 'panel';
      panel.innerHTML = `
        <div class="panel-header" style="justify-content:space-between"><span>Document folders</span><button class="btn btn-primary" id="add-folder-btn">+ Add folder</button></div>
        <div class="panel-body" id="folders-list"></div>
      `;
      page.appendChild(panel);
      const list = $('#folders-list');
      if (!folders.length) {
        list.innerHTML = '<p class="empty-state">No folders found.</p>';
      } else {
        folders.forEach((f) => {
          const row = document.createElement('div');
          row.className = 'group-card';
          row.innerHTML = `
            <div class="group-title">
              <span>${escapeHtml(f.name)} ${f.is_default ? '<span class="badge badge-gray">Default</span>' : ''}</span>
              <span class="badge ${f.active ? 'badge-green' : 'badge-gray'}">${f.active ? 'Active' : 'Inactive'}</span>
            </div>
          `;
          if (!f.is_default && isOrgAdmin) {
            const btn = document.createElement('button');
            btn.className = 'btn';
            btn.style.marginTop = '8px';
            btn.textContent = f.active ? 'Inactivate' : 'Activate';
            btn.addEventListener('click', () => toggleFolder(f));
            row.appendChild(btn);
          }
          list.appendChild(row);
        });
      }
      $('#add-folder-btn').addEventListener('click', openFolderModal);
    } catch (err) {
      page.innerHTML = '<p class="empty-state">Unable to load folders.</p>';
    }
  }

  async function loadDistributions() {
    const page = $('#doc-tab-distributions');
    page.innerHTML = '<p class="empty-state">Loading distributions...</p>';
    try {
      const endpoint = isOrgAdmin ? '/admin/organizations/' + state.orgId + '/documents/distributions' : '/documents/distributions';
      const res = await api(endpoint);
      const dists = res.distributions || [];
      page.innerHTML = '';
      const panel = document.createElement('div');
      panel.className = 'panel';
      panel.innerHTML = '<div class="panel-header">Recent document distributions</div><div class="panel-body" id="distributions-table-wrap"></div>';
      page.appendChild(panel);
      const wrap = $('#distributions-table-wrap');
      if (!dists.length) {
        wrap.innerHTML = '<p class="empty-state">No distributions found.</p>';
        return;
      }
      const table = document.createElement('table');
      table.innerHTML = `<thead><tr><th>Document</th><th>Distributed by</th><th>Scope</th><th>Recipients</th><th>Available</th><th>Status</th></tr></thead><tbody></tbody>`;
      const tbody = table.querySelector('tbody');
      dists.forEach((d) => {
        const now = new Date();
        const available = new Date(d.available_at) <= now;
        const expired = d.expires_at && new Date(d.expires_at) <= now;
        const status = expired ? 'Expired' : available ? 'Available' : 'Future';
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td><div class="name">${escapeHtml(d.document_title || '—')}</div><div class="subtext">Version ${d.version_number}</div></td>
          <td>${escapeHtml(d.created_by_name || '—')}</td>
          <td>${escapeHtml(d.distribution_type)}${d.scope_id ? ' (' + d.scope_id.slice(0, 8) + ')' : ''}</td>
          <td>${d.recipient_count}</td>
          <td>${new Date(d.available_at).toLocaleString()}</td>
          <td><span class="badge ${expired ? 'badge-gray' : available ? 'badge-green' : 'badge-amber'}">${status}</span></td>
        `;
        tbody.appendChild(tr);
      });
      wrap.innerHTML = '';
      wrap.appendChild(table);
    } catch (err) {
      page.innerHTML = '<p class="empty-state">Unable to load distributions.</p>';
    }
  }

  function addBtn(parent, text, cls, onClick) {
    const btn = document.createElement('button');
    btn.className = cls;
    btn.textContent = text;
    btn.style.marginLeft = '6px';
    btn.addEventListener('click', onClick);
    parent.appendChild(btn);
  }

  async function toggleDocStatus(d) {
    await api('/documents/' + d.id, { method: 'PATCH', body: { status: d.status === 'active' ? 'inactive' : 'active' } });
    loadLibrary();
  }

  async function toggleFolder(f) {
    await api('/admin/organizations/' + state.orgId + '/documents/folders/' + f.id, { method: 'PATCH', body: { active: !f.active } });
    loadFolders();
  }

  function openUploadModal(folders) {
    openModal('upload-doc-modal', `
      <div class="modal-header"><strong>Upload organization document</strong><button class="btn" id="close-upload">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Document title</label><input id="upload-title" placeholder="Document title"></div>
        <div class="field"><label>Folder</label><select id="upload-folder">${folders.map((f) => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}</select></div>
        <div class="field"><label>File</label><input type="file" id="upload-file" accept=".pdf,.docx"></div>
        <div class="field"><label>Description</label><input id="upload-description" placeholder="Optional description"></div>
        <div class="error-text" id="upload-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-upload">Cancel</button>
        <button class="btn btn-primary" id="submit-upload">Upload document</button>
      </div>
    `);
    $('#close-upload').addEventListener('click', () => closeModal('upload-doc-modal'));
    $('#cancel-upload').addEventListener('click', () => closeModal('upload-doc-modal'));
    $('#submit-upload').addEventListener('click', async () => {
      const file = $('#upload-file').files[0];
      const errorEl = $('#upload-error');
      if (!file) { errorEl.textContent = 'Please select a file.'; return; }
      const form = new FormData();
      form.append('file', file);
      form.append('title', $('#upload-title').value || file.name);
      form.append('folderId', $('#upload-folder').value);
      form.append('description', $('#upload-description').value);
      try {
        const res = await fetch(`${tenantPrefix()}/api/documents`, { method: 'POST', body: form, credentials: 'same-origin' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          errorEl.textContent = data.message || 'Upload failed. Only PDF and DOCX files are supported.';
          return;
        }
        closeModal('upload-doc-modal');
        loadLibrary();
      } catch (err) {
        errorEl.textContent = 'Upload failed. Only PDF and DOCX files are supported.';
      }
    });
  }

  function openVersionModal(d) {
    openModal('version-doc-modal', `
      <div class="modal-header"><strong>Upload new version</strong><button class="btn" id="close-version">Close</button></div>
      <div class="modal-body">
        <div class="field"><label>Document</label><input value="${escapeHtml(d.title)}" disabled></div>
        <div class="field"><label>Current version</label><input value="${d.version_number || '—'}" disabled></div>
        <div class="field"><label>Replacement file</label><input type="file" id="version-file" accept=".pdf,.docx"></div>
        <div class="error-text" id="version-error"></div>
      </div>
      <div class="modal-footer">
        <button class="btn" id="cancel-version">Cancel</button>
        <button class="btn btn-primary" id="submit-version">Upload new version</button>
      </div>
    `);
    $('#close-version').addEventListener('click', () => closeModal('version-doc-modal'));
    $('#cancel-version').addEventListener('click', () => closeModal('version-doc-modal'));
    $('#submit-version').addEventListener('click', async () => {
      const file = $('#version-file').files[0];
      const errorEl = $('#version-error');
      if (!file) { errorEl.textContent = 'Please select a file.'; return; }
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await fetch(`${tenantPrefix()}/api/documents/${d.id}/versions`, { method: 'POST', body: form, credentials: 'same-origin' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          errorEl.textContent = data.message || 'Upload failed. Only PDF and DOCX files are supported.';
          return;
        }
        closeModal('version-doc-modal');
        loadLibrary();
      } catch (err) {
        errorEl.textContent = 'Upload failed. Only PDF and DOCX files are supported.';
      }
    });
  }

  let scopes = { groups: [], clients: [], sessions: [], canDistributeToOrganization: false };
  async function refreshScopes() {
    scopes = await api('/documents/distribution-scopes');
  }

  function openDistributeModal(d, folders, _scopes) {
    refreshScopes().then(() => {
      const scopeOptions = `
        <div class="radio-card"><label class="radio-line"><input type="radio" name="dist-scope" value="individual" checked>
          <div><div class="name">Individual participant</div><div class="note">Send to one active participant.</div></div></label></div>
        <div class="radio-card"><label class="radio-line"><input type="radio" name="dist-scope" value="group">
          <div><div class="name">Group</div><div class="note">Send to current members of a selected group.</div></div></label></div>
        <div class="radio-card"><label class="radio-line"><input type="radio" name="dist-scope" value="session">
          <div><div class="name">Scheduled session</div><div class="note">Send to participants associated with a specific session occurrence.</div></div></label></div>
        ${scopes.canDistributeToOrganization ? `<div class="radio-card"><label class="radio-line"><input type="radio" name="dist-scope" value="organization"><div><div class="name">All active participants</div><div class="note">Organization-wide distribution.</div></div></label></div>` : ''}
      `;
      openModal('distribute-doc-modal', `
        <div class="modal-header"><strong>Distribute document</strong><button class="btn" id="close-distribute">Close</button></div>
        <div class="modal-body">
          <div class="field"><label>Document</label><input value="${escapeHtml(d.title)} - Version ${d.version_number}" disabled></div>
          <div class="field"><label>Distribution scope</label>${scopeOptions}</div>
          <div class="field"><label>Recipient / group / session</label><select id="dist-target"></select></div>
          <div class="field"><label>Available starting</label><input type="datetime-local" id="dist-available"></div>
          <div class="field"><label>Expiration (optional)</label><input type="datetime-local" id="dist-expires"></div>
        </div>
        <div class="modal-footer">
          <button class="btn" id="cancel-distribute">Cancel</button>
          <button class="btn btn-primary" id="submit-distribute">Distribute</button>
        </div>
      `);
      $('#close-distribute').addEventListener('click', () => closeModal('distribute-doc-modal'));
      $('#cancel-distribute').addEventListener('click', () => closeModal('distribute-doc-modal'));
      $('#dist-available').value = toDateTimeLocal(new Date());
      const updateTarget = () => {
        const type = document.querySelector('input[name="dist-scope"]:checked').value;
        const sel = $('#dist-target');
        sel.innerHTML = '';
        let items = [];
        if (type === 'individual') items = scopes.clients;
        if (type === 'group') items = scopes.groups;
        if (type === 'session') items = scopes.sessions;
        if (type === 'organization') { sel.innerHTML = '<option>All active participants</option>'; sel.disabled = true; return; }
        sel.disabled = false;
        items.forEach((it) => {
          const opt = document.createElement('option');
          opt.value = it.id;
          opt.textContent = it.name;
          sel.appendChild(opt);
        });
      };
      $$('input[name="dist-scope"]').forEach((r) => r.addEventListener('change', updateTarget));
      updateTarget();
      $('#submit-distribute').addEventListener('click', async () => {
        const type = document.querySelector('input[name="dist-scope"]:checked').value;
        const scopeId = type === 'organization' ? null : $('#dist-target').value;
        const body = {
          documentVersionId: d.version_id,
          distributionType: type,
          scopeId,
          availableAt: $('#dist-available').value ? new Date($('#dist-available').value).toISOString() : null,
          expiresAt: $('#dist-expires').value ? new Date($('#dist-expires').value).toISOString() : null,
        };
        await api('/documents/distribute', { method: 'POST', body });
        closeModal('distribute-doc-modal');
        showDocTab('distributions');
        loadDistributions();
      });
    });
  }

  function openFolderModal() {
    openModal('folder-doc-modal', `
      <div class="modal-header"><strong>Add folder</strong><button class="btn" id="close-folder">Close</button></div>
      <div class="modal-body"><div class="field"><label>Folder name</label><input id="folder-name" placeholder="Folder name"></div></div>
      <div class="modal-footer"><button class="btn" id="cancel-folder">Cancel</button><button class="btn btn-primary" id="submit-folder">Add folder</button></div>
    `);
    $('#close-folder').addEventListener('click', () => closeModal('folder-doc-modal'));
    $('#cancel-folder').addEventListener('click', () => closeModal('folder-doc-modal'));
    $('#submit-folder').addEventListener('click', async () => {
      const name = $('#folder-name').value.trim();
      if (!name) return;
      await api('/admin/organizations/' + state.orgId + '/documents/folders', { method: 'POST', body: { name } });
      closeModal('folder-doc-modal');
      showDocTab('folders');
      loadFolders();
    });
  }

  function toDateTimeLocal(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  loadLibrary();
}

/* -------------------------------------------------------------------------- */
/* Init                                                                       */
/* -------------------------------------------------------------------------- */
async function init() {
  $('#login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    clearError();
    const username = $('#login-username').value;
    const password = $('#login-password').value;
    try {
      await api('/auth/login', { method: 'POST', body: { username, password } });
      const ok = await refreshAuth();
      if (ok) {
        const params = new URLSearchParams(window.location.search);
        const page = params.get('page');
        const defaultPage = state.orgId
          ? (isProvider() && !hasPermission('organization.manage') ? 'my-sessions' : 'dashboard')
          : 'platform';
        navigateTo(page || defaultPage);
      }
    } catch (err) {
      $('#login-error').textContent = 'Invalid username or password.';
    }
  });

  $('#logout-btn').addEventListener('click', async () => {
    await api('/auth/logout', { method: 'POST' });
    showLogin();
  });

  $('#org-select').addEventListener('change', (e) => switchOrg(e.target.value));

  $('#reassign-cancel').addEventListener('click', () => $('#reassign-modal').classList.remove('open'));

  $('#notification-btn').addEventListener('click', () => openDrawer());
  $('#notification-close').addEventListener('click', () => closeDrawer());
  $('#notification-drawer-backdrop').addEventListener('click', () => closeDrawer());
  $('#notification-mark-all-read').addEventListener('click', (e) => markAllRead(e));
  $('#notification-view-all').addEventListener('click', () => { closeDrawer(); navigateTo('notifications'); });

  const ok = await refreshAuth();
  if (ok) {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    const defaultPage = state.orgId
      ? (isProvider() && !hasPermission('organization.manage') ? 'my-sessions' : 'dashboard')
      : 'platform';
    navigateTo(page || defaultPage);
  }
}

window.addEventListener('beforeunload', stopDevicePreview);

init();
